// Importing required components and hooks from libraries
import { Button, Paper, TextField, useMediaQuery } from "@mui/material";
import { DoubleArrow } from "@mui/icons-material";
import { useTheme } from "@mui/styles";
import { AnimatePresence } from "framer-motion";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import LoadingOverlay from "react-loading-overlay-ts";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ProgressBar from "react-scroll-progress-bar";
import { config, useSpring } from "react-spring";
import { toast } from "react-toastify";
import { ThemeUpdateContext } from "../../../App";
import { useIdleTimer } from "react-idle-timer";
import {
  // Redux actions for manipulating survey data
  addAnswer,
  addContactDetails,
  addDirectorMessage,
  clearAnsweres,
  removeAnswer,
  setSurveyTheme,
} from "../../../redux/survey/actions";
import { getDetails } from "../../../webRatingNew/apiHelpers/frontRating";

// Commonly used components for the survey
import BackgroundSecond from "../../common/components/BackgroundSecond";
import QuestionWrapper from "../../common/components/QuestionWrapper";
import Rating from "../../common/components/Rating";
import { commonPageMakeStyles } from "../../common/styles/commonPageStyles";

// Helper functions for making API calls
import { axiosPostData, postData } from "../../helpers/fetch_services";

// Specific components for various types of survey questions
import { isMobile } from "react-device-detect";
import store from "../../../redux/store";
import ClientConnectDirector from "../ClientConnectDirector";
import ContactForm from "../ContactForm";
import ErrorBanner from "../ErrorBanner/ErrorBanner";
import PhoneNumberInput, {
  isValidEmployeePhone,
  getPhoneValidationMessage,
  getPhoneCountry,
  formatPhoneAnswerForPayload,
  getPhoneDialCode,
} from "components/common/PhoneNumberInput";
import { getCompanyCountryCode } from "helpers/companyCountryHelper";
import { setServeFirstBrandingHidden } from "helpers/serveFirstBranding";
import { SERVE_FIRST_LOGO_URL } from "../../../constants/branding";
import Animate from "./Animate";
import BottomProgress from "./BottomProgress";
import Emoji from "./Emoji/Emoji";
import EmployeeSelect from "./EmployeeSelect";
import MultipleSelect from "./MultipleSelect";
import NotesAndImage from "./NotesAndImage";
import Radio from "./Radio";
import GridSpot from "./GridSpot";
import Sequence from "./Sequence/Sequence";
import StarRating from "./StarRating";
import TextArea from "./TextArea";
import Upload from "./Upload";
import NumberType from "./NumberType";
import SignatureInput from "./SignatureInput";
import "./questions.css";
import {
  attemptCloseTabOrRedirect,
  calculateExpirationTime,
  isCompanyLogin,
} from "../../helpers/Utils";
import moment from "moment";
import InventoryType from "./InventoryType";
import { useIntl } from "react-intl";
import Dropdown from "./Dropdown";
import SupportLang from "../../../Common/SupportLang";
import { handleSupportedLanguages } from "../../../lang";
import GridType from "./GridType";
import {
  savePartialResponse,
  savePartialResponseOnExit,
} from "../../helpers/savePartialSurveyAnswers";
import AutoSubmitErrorPopup from "../AutoSubmitErrorPopup";
import {
  filterQuestionsByScheduleWindows,
  removeEmptyOrNullValuesFromAnswersCopy,
} from "../../helpers/surveyHelper";
import { getGridspotConfigurationError } from "../../helpers/gridspot";
import {
  normalizeQuestionIndices,
  toQuestionIndex,
} from "../../helpers/surveyQuestionVisibility";
import {
  CSAT_ERROR_CODES,
  csatErrorCode,
  getCsatToken,
  withCsatToken,
} from "../../../helpers/csatToken";

// Main React component for rendering survey questions
function Question(props) {
  const intl = useIntl();
  // Destructuring props to extract relevant data
  const {
    survey,
    answeres,
    locations,
    directorMessage,
    survey_without_login,
    setSurveyTheme_,
    clearAnsweres_,
    addAnswer_,
    removeAnswer_,
    addContactDetails_,
    addDirectorMessage_,
    contactDetails,
  } = props;
  // Utilizing Context, Router, and Theme hooks
  const { changeTheme } = useContext(ThemeUpdateContext);
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();

  const surveyCount = localStorage.getItem("total_location_survey")
    ? parseInt(localStorage.getItem("total_location_survey"))
    : 0;

  // Extracting and setting states from location and theme
  const surveyWithoutLogin =
    location.state !== undefined ? location.state.surveyWithoutLogin : "";
  const hasClientConnect =
    location.state !== undefined ? location.state.clientConnect : null;
  const visitDate =
    location.state !== undefined ? location.state.visitDate : null;
  const visitTimeIn =
    location.state !== undefined ? location.state.visitTimeIn : null;
  const guest_info =
    location.state !== undefined ? location.state.guest_info : null;

  const isMob = useMediaQuery(theme.breakpoints.down("sm"));
  // Custom styles for the survey page
  const commonPageCss = commonPageMakeStyles();

  // State hooks for local state management
  const [loading, setLoading] = useState(true);
  const [empLoading, setEmpLoading] = useState(true);

  // Various states for managing survey questions, types, and navigation
  const [qPerPage, setQPerPage] = useState();
  const [totalPage, setTotalPage] = useState(0);
  const [allQuestionsOriginal, setAllQuestionsOriginal] = useState([]);
  const [currentSurvey, setCurrentSurvey] = useState({});
  const [allQuestions, setAllQuestions] = useState([]);
  const [surveyType, setSurveyType] = useState("");
  const [page, setPage] = React.useState(1);
  const [direction, setDirection] = React.useState(1);
  const [logo, setLogo] = useState(SERVE_FIRST_LOGO_URL);
  const [greetingStatus, setGs] = useState(false);
  // State and hook for managing cookies
  // eslint-disable-next-line no-unused-vars
  const [cookies, setCookie] = useCookies(["survey"]);
  const [surveyResponseCookie, setSurveyResponseCookie] = useCookies([
    "survey_response",
  ]);
  // const [pageBreaks, setPageBreaks] = useState([]);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);

  // Visit Time Out state
  const [selectedTimeOut, setSelectedTimeOut] = useState(null);

  const [showContactForm, setShowContactForm] = useState(false);
  const [contactFormData, setContactFormData] = useState({});
  const [contactFormErrors, setContactFormErrors] = useState({});
  const [showErrorPopup, setShowErrorPopup] = useState({
    title: "",
    message: "",
    errors: [],
    show: false,
  });

  // Calculating question indexes for pagination
  // var trimStart = (page - 1) * qPerPage;
  // var trimEnd = trimStart + qPerPage;

  const getPageBreaks = (
    questions,
    type = null,
    includeContactForm = false,
  ) => {
    let pageBreaks = [];
    let calculatedTotalPages = 0;
    if (questions?.length) {
      const isNotPageBreak = questions
        ?.filter((q) => q.questionType !== "director_message")
        ?.every((que) => !que?.page_break);
      if (questions?.length && isNotPageBreak) {
        let quePerPage = survey_without_login?.questions_per_page;
        if (survey_without_login?.surveyType === "tablet") {
          setQPerPage(1);

          // if (survey_without_login?.leadershipUser) {
          //   setTotalPage(questions?.length + 1);
          // } else {
          calculatedTotalPages = questions?.length;
          // Add extra page for contact form if enabled
          if (includeContactForm) {
            calculatedTotalPages += 1;
          }
          setTotalPage(calculatedTotalPages);
          // }
          quePerPage = 1;
        } else {
          setQPerPage(quePerPage);
          // const totalPages = Math.ceil(questions?.length / quePerPage);
          // if (survey_without_login?.leadershipUser) {
          //   setTotalPage(totalPages + 1);
          // } else {
          calculatedTotalPages = Math.ceil(questions?.length / quePerPage);
          // Add extra page for contact form if enabled
          if (includeContactForm) {
            calculatedTotalPages += 1;
          }
          setTotalPage(calculatedTotalPages);
          // }
        }
        const trimStart = (page - 1) * quePerPage;
        setTrimStart(trimStart);
        const trimEnd = trimStart + quePerPage - 1;

        setTrimEnd(trimEnd);
      } else {
        const filteredQues = questions?.filter(
          (q) => q?.questionType !== "skip",
        );
        filteredQues?.forEach((question, index) => {
          // Check if the current question is the last one
          const isLastQuestion = index === filteredQues?.length - 1;
          // Check if the question is of type "director_message"
          const isDirectorMessage =
            question.questionType === "director_message";
          // Check conditions for page breaks
          if (question.page_break && type !== "tablet") {
            // If last question and director_message, add page break
            if (isLastQuestion) {
              if (isDirectorMessage) {
                pageBreaks.push(index - 1);
              }
              if (includeContactForm) {
                pageBreaks.push(index);
              }
            } else if (!isLastQuestion) {
              // If not last question, add page break
              pageBreaks.push(index);
            }
          } else if (type === "tablet" && !isLastQuestion) {
            // If tablet type and not last question, add page break
            pageBreaks.push(index);
          }
        });
        pageBreaks = [...new Set(pageBreaks)];

        // Calculate trimStart based on page number
        const trimStart = page - 1 === 0 ? 0 : pageBreaks[page - 2] + 1;
        setTrimStart(trimStart);
        // Set total number of pages
        calculatedTotalPages = pageBreaks.length + 1;
        setTotalPage(calculatedTotalPages);

        // Calculate trimEnd based on page number
        const trimEnd =
          page === calculatedTotalPages
            ? questions.length - 1
            : pageBreaks[page - 1];
        setTrimEnd(trimEnd);

        // Calculate number of questions per page
        const questionsPerPage = trimEnd - trimStart + 1;

        setQPerPage(questionsPerPage);
      }
    }

    // Return array of page breaks
    return { pageBreaks, totalPages: calculatedTotalPages };
  };

  useEffect(() => {
    getPageBreaks(allQuestions, surveyType, showContactForm);
  }, [page, showContactForm]);

  // States for managing contact details and their validation
  const [isHelpDesk, setIsHelpDesk] = useState(
    Object.keys(survey).includes("contactDetails") &&
      (survey.contactDetails.email !== null ||
        survey.contactDetails.phone !== null),
  );
  const [contactDetail, setContactDetail] = useState({
    name: null,
    email: null,
    phone: "",
  });
  const defaultContactErrors = {
    name: { error: false, message: "" },
    email: { error: false, message: "" },
    phone: { error: false, message: "" },
    emailOrPhone: { error: false, message: "" },
    emailValid: { error: false, message: "" },
  };
  const [contactDetailErrors, setContactDetailErrors] =
    useState(defaultContactErrors);

  // Function to update contact details
  const updateContactDetails = (e, key) => {
    let { value } = e.target;
    if (key) {
      if (key === "email") {
        if (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(value)) {
          updateContactDetailError("emailValid", true, "");
        } else {
          updateContactDetailError("emailValid", false, "");
        }
      }
      setContactDetail((prev) => ({ ...prev, [key]: value }));
    }
  };

  const [customValues, setCustomValues] = useState([]);
  const updateCustomValues = (value, item) => {
    setCustomValues((prev) => ({ ...prev, [item.fieldKey]: value }));
    // remove from the customValuesErrors
    setCustomValuesErrors((prev) => ({
      ...prev,
      [item.fieldKey]: { error: false, message: "" },
    }));
  };

  const updateContactFormData = (value, fieldKey) => {
    setContactFormData((prev) => ({ ...prev, [fieldKey]: value }));
    setContactFormErrors((prev) => ({
      ...prev,
      [fieldKey]: { error: false, message: "" },
    }));
  };

  // Function to update contact detail errors
  const updateContactDetailError = (key, value, message = "") => {
    if (key) {
      setContactDetailErrors((prev) => ({
        ...prev,
        [key]: { error: value, message },
      }));
    }
  };

  // Function to save contact details and validate them
  const saveContactDetails = () => {
    let hasErrors = false;

    if (!(contactDetail?.email || contactDetail?.phone)) {
      updateContactDetailError(
        "emailOrPhone",
        true,
        intl.formatMessage({
          id: "please-enter-your-email-address-or-phone-number",
        }),
      );
      return;
    }

    // Validate email
    if (
      contactDetail?.email &&
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(contactDetail.email)
    ) {
      updateContactDetailError(
        "email",
        true,
        intl.formatMessage({ id: "invalid-email-address" }),
      );
      updateContactDetailError("emailValid", false, "");
      hasErrors = true;
    } else {
      updateContactDetailError("email", false, "");
    }

    // Validate phone
    if (contactDetail?.phone) {
      const countryHint =
        getPhoneCountry(contactDetail.phone, contactDetail.phoneCountry) ||
        contactDetail.phoneCountry ||
        getCompanyCountryCode();
      if (!isValidEmployeePhone(contactDetail.phone, countryHint)) {
        updateContactDetailError(
          "phone",
          true,
          getPhoneValidationMessage(intl, countryHint, contactDetail.phone),
        );
        hasErrors = true;
      }
    } else {
      updateContactDetailError("phone", false, "");
    }

    if (hasErrors) {
      return;
    }
    // Add contact details to Redux (phone already E.164 from PhoneNumberInput)
    addContactDetails_(contactDetail);
    setIsHelpDesk(false);
    if (isMobile && survey?.survey_without_login?.allow_conversational_view) {
      history.push({
        pathname: "/mobile-survey/questions",
        state: {
          surveyWithoutLogin: surveyWithoutLogin,
          visitDate: visitDate || null,
          visitTimeIn: visitTimeIn ? visitTimeIn : null,
          guest_info: guest_info || null,
        },
      });
    }
  };

  // Function to set brand-specific details
  const setBrandDetails = async (survey_id) => {
    const result = await postData("api/getBrandDetail", {
      survey_id,
    });
    if (result?.status === 200) {
      setServeFirstBrandingHidden(result?.data?.hideServeFirst);
      setLogo(
        result?.data?.logo
          ? result?.data?.logo
          : SERVE_FIRST_LOGO_URL,
      );
      if (result?.data?.logo) {
        localStorage.setItem("logo", result?.data?.logo);
      }
      setSurveyTheme_({
        primaryColor:
          result?.data?.primaryColor !== "undefined"
            ? result?.data?.primaryColor
            : "#36473F",
        secondaryColor:
          result?.data?.secondaryColor !== "undefined"
            ? result?.data?.secondaryColor
            : "#8FA280",
        backgroundColor:
          result?.data?.backgroundColor !== "undefined"
            ? result?.data?.backgroundColor
            : "#36473F",
        highlightColor:
          result?.data?.highightColor !== "undefined"
            ? result?.data?.highightColor
            : "#8FA280",
        fontColor:
          result?.data?.fontColor !== "undefined"
            ? result?.data?.fontColor
            : "#3a3a3a",
      });
      changeTheme({
        primaryColor:
          result?.data?.primaryColor !== "undefined"
            ? result?.data?.primaryColor
            : "#36473F",
        secondaryColor:
          result?.data?.secondaryColor !== "undefined"
            ? result?.data?.secondaryColor
            : "#8FA280",
        backgroundColor:
          result?.data?.backgroundColor !== "undefined"
            ? result?.data?.backgroundColor
            : "#36473F",
        highlightColor:
          result?.data?.highightColor !== "undefined"
            ? result?.data?.highightColor
            : "#8FA280",
        fontColor:
          result?.data?.fontColor !== "undefined"
            ? result?.data?.fontColor
            : "#3a3a3a",
      });
    }
  };

  useEffect(() => {
    changeTheme({
      primaryColor: props.survey_theme.primaryColor,
      secondaryColor: props.survey_theme.secondaryColor,
      backgroundColor: props.survey_theme.backgroundColor,
      highlightColor: props.survey_theme.highlightColor,
      fontColor: props.survey_theme.fontColor,
    });

    const locationId = localStorage.getItem("location");
    const surveyId = localStorage.getItem("survey");

    //Set Logo and Brand detail
    setBrandDetails(surveyId);

    if (surveyWithoutLogin) {
      const results = JSON.parse(JSON.stringify(survey_without_login));
      // Keep the full API question list for hide_questions / route_to_index indices.
      // Location and schedule filters apply only to the visible list.
      const questionsOriginal = [...(results.questions || [])];
      let visibleQuestions = [...questionsOriginal];
      if (!locationId) {
        visibleQuestions = visibleQuestions.filter(
          (item) =>
            item.questionType !== "employees" &&
            item.questionType !== "inventory",
        );
      }
      visibleQuestions = filterQuestionsByScheduleWindows(visibleQuestions);

      setCurrentSurvey(results);

      const needsDirectorMessage =
        results?.leadershipUser ||
        results?.visit_information ||
        results?.competitionInformation;

      if (results.surveyType === "tablet") {
        // setQPerPage(1);
        setSurveyType(results.surveyType);
        const pageBreaks = getPageBreaks(
          needsDirectorMessage
            ? [
                ...visibleQuestions,
                { questionType: "director_message", page_break: true },
              ]
            : [...visibleQuestions],
          "tablet",
          false,
        );
        const totalPages = pageBreaks?.length + 1;

        setAllQuestionsOriginal(questionsOriginal);
        if (needsDirectorMessage) {
          // setTotalPage(totalPages);
          setAllQuestions([
            ...visibleQuestions,
            { questionType: "director_message", page_break: true },
          ]);
        } else {
          // setTotalPage(totalPages);
          setAllQuestions(visibleQuestions);
        }
      } else {
        setSurveyType(results.surveyType);
        // setQPerPage(results.questions_per_page);
        // const totalPages = Math.ceil(
        //   results.questions.length / results.questions_per_page
        // );

        const pageBreaks = getPageBreaks(
          needsDirectorMessage
            ? [
                ...visibleQuestions,
                { questionType: "director_message", page_break: true },
              ]
            : [...visibleQuestions],
          null,
          false,
        );
        const totalPages = pageBreaks?.length + 1;

        if (needsDirectorMessage) {
          const offset =
            (totalPages * results.questions_per_page) %
            questionsOriginal.length;
          // setTotalPage(totalPages);
          setAllQuestions([
            ...visibleQuestions,
            // ...Array.from({ length: offset }, (_, i) => i).map(() => ({
            //   questionType: "skip",
            // })),
            { questionType: "director_message", page_break: true },
          ]);
        } else {
          // setTotalPage(
          //   Math.ceil(results.questions.length / results.questions_per_page)
          // );
          // setTotalPage(totalPages);
          setAllQuestions(visibleQuestions);
        }
        setAllQuestionsOriginal(questionsOriginal);
      }

      if (results?.help_desk === true) {
        setIsHelpDesk(true);
      }

      if (
        isMobile &&
        survey?.survey_without_login?.allow_conversational_view &&
        !isHelpDesk &&
        !results?.help_desk
      ) {
        history.push({
          pathname: "/mobile-survey/questions",
          state: {
            surveyWithoutLogin: survey || currentSurvey,
            visitDate: visitDate || null,
            visitTimeIn: visitTimeIn ? visitTimeIn : null,
            guest_info: guest_info || null,
          },
        });
      }
    } else if (locations.length && locationId) {
      if (surveyId) {
        const surveyResponse = surveyResponseCookie.survey_response;
        if (!getCsatToken(surveyId) && surveyResponse?.survey_id === surveyId) {
          history.push("/sameDaySurvey", { cookieData: surveyResponse });
        } else {
          getQuestions();
        }
      } else {
        toast(intl.formatMessage({ id: "please-select-survey" }));
        history.push("/survey/audition");
      }
    } else {
      toast(intl.formatMessage({ id: "please-select-location" }));
      history.push("/survey/location");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!greetingStatus) setTimeout(() => setGs(true), 500);
  }, [page]);
  const slideProps = useSpring({
    from: { transform: "translateX(-100%)", opacity: 0 },
    to: [{ transform: "translateX(0)", opacity: 1 }],
    // reset: true,
    config: { duration: 400, ...config.molasses },
  });
  const fadeProps = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    // reset: true,
    config: { duration: 100, ...config.molasses },
  });
  const [Oldquestions, setOldquestions] = useState();

  const getQuestions = async () => {
    // Retrieve location and survey IDs from local storage
    const locationId = localStorage.getItem("location");
    const surveyId = localStorage.getItem("survey");
    let selectedSurvey;

    // If location ID exists, filter locations array to find selected location
    if (locationId) {
      const result = await postData("frontEmployeeFeedback/getSurvey", {
        survey_id: surveyId,
        // CSAT email link: checked again here, as on Login (409 / 410).
        csat_token: getCsatToken(surveyId) || undefined,
      });
      const csatCode = csatErrorCode(result);
      if (csatCode === CSAT_ERROR_CODES.ALREADY_SUBMITTED) {
        history.push("/sameDaySurvey");
        return;
      }
      if (csatCode === CSAT_ERROR_CODES.TOKEN_INVALID) {
        toast.error(intl.formatMessage({ id: "csat-link-invalid" }));
        return;
      }
      if (result?.success) {
        selectedSurvey = result?.data;
      }
    }
    // If a selected survey is found
    if (selectedSurvey) {
      const results = selectedSurvey;
      setCurrentSurvey(results);
      handleSupportedLanguages(results?.supported_languages);

      // Keep full original list for hide_questions / route_to_index indices.
      // Schedule windows only affect the visible list.
      const visibleQuestions = filterQuestionsByScheduleWindows(
        results.questions,
      );

      // Check survey type (tablet or not)
      if (results.surveyType === "tablet") {
        // Process tablet survey
        setSurveyType(results.surveyType);
        setAllQuestionsOriginal(results.questions);
        // Calculate page breaks for tablet survey
        const pageBreaks = getPageBreaks(
          // Append director message question if survey has leadership user
          survey_without_login?.leadershipUser ||
            survey_without_login?.visit_information ||
            survey_without_login?.competitionInformation
            ? [
                ...visibleQuestions,
                { questionType: "director_message", page_break: true },
              ]
            : [...visibleQuestions],
          "tablet",
          false, // Contact form state determined later
        );
        const totalPages = pageBreaks?.length + 1;
        // Set all questions for tablet survey, including director message question if needed
        if (results?.leadershipUser || results?.visit_information) {
          setAllQuestions([
            ...visibleQuestions,
            { questionType: "director_message", page_break: true },
          ]);
        } else {
          setAllQuestions(visibleQuestions);
        }
      } else {
        // Process non-tablet survey
        setSurveyType(results.surveyType);
        setOldquestions(results.questions);
        setAllQuestionsOriginal(results.questions);
        // Calculate page breaks for non-tablet survey
        const pageBreaks = getPageBreaks(
          // Append director message question if survey has leadership user
          survey_without_login?.leadershipUser ||
            survey_without_login?.visit_information ||
            survey_without_login?.competitionInformation
            ? [
                ...visibleQuestions,
                { questionType: "director_message", page_break: true },
              ]
            : [...visibleQuestions],
          null,
          false,
        );

        const totalPages = pageBreaks?.length + 1;
        if (
          results?.leadershipUser ||
          results?.visit_information ||
          results?.competitionInformation
        ) {
          const offset =
            (totalPages * results.questions_per_page) %
            results.questions.length;
          setAllQuestions([
            ...visibleQuestions,
            // ...Array.from({ length: offset }, (_, i) => i).map(() => ({
            //   questionType: "skip",
            // })),
            { questionType: "director_message", page_break: true },
          ]);
        } else {
          setAllQuestions(visibleQuestions);
        }
      }
      // Set help desk flag if help desk is enabled for the survey
      if (results?.help_desk === true) {
        setIsHelpDesk(true);
      }
    }
  };

  const validateContactForm = () => {
    if (!showContactForm) return true;

    const { contact_fields, contact_custom_fields } =
      survey_without_login || {};
    let hasErrors = false;

    // Validate standard contact fields
    if (contact_fields?.name?.enabled && contact_fields?.name?.required) {
      if (!contactFormData?.name?.trim()) {
        setContactFormErrors((prev) => ({
          ...prev,
          name: {
            error: true,
            message: intl.formatMessage({ id: "please-enter-your-name" }),
          },
        }));
        hasErrors = true;
      }
    }

    if (contact_fields?.email?.enabled) {
      if (contact_fields?.email?.required && !contactFormData?.email) {
        setContactFormErrors((prev) => ({
          ...prev,
          email: {
            error: true,
            message: intl.formatMessage({ id: "please-enter-your-email" }),
          },
        }));
        hasErrors = true;
      } else if (
        contactFormData.email &&
        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(
          contactFormData.email,
        )
      ) {
        setContactFormErrors((prev) => ({
          ...prev,
          email: {
            error: true,
            message: intl.formatMessage({ id: "invalid-email-address" }),
          },
        }));
        hasErrors = true;
      }
    }

    if (contact_fields?.phone?.enabled && contact_fields?.phone?.required) {
      if (!contactFormData?.phone) {
        setContactFormErrors((prev) => ({
          ...prev,
          phone: {
            error: true,
            message: intl.formatMessage({ id: "please-enter-your-phone" }),
          },
        }));
        hasErrors = true;
      } else {
        const countryHint =
          getPhoneCountry(
            contactFormData.phone,
            contactFormData.phoneCountry,
          ) ||
          contactFormData.phoneCountry ||
          getCompanyCountryCode();
        if (!isValidEmployeePhone(contactFormData.phone, countryHint)) {
          setContactFormErrors((prev) => ({
            ...prev,
            phone: {
              error: true,
              message: getPhoneValidationMessage(
                intl,
                countryHint,
                contactFormData.phone,
              ),
            },
          }));
          hasErrors = true;
        }
      }
    }

    // Validate custom contact fields
    if (contact_custom_fields?.length > 0) {
      contact_custom_fields.forEach((field) => {
        if (field.isRequired && !contactFormData[field.fieldKey]) {
          setContactFormErrors((prev) => ({
            ...prev,
            [field.fieldKey]: {
              error: true,
              message: `${intl.formatMessage({
                id: "please-enter",
              })} ${field.fieldName.toLowerCase()}`,
            },
          }));
          hasErrors = true;
        }

        // Email validation for custom email fields
        if (
          field.fieldType === "email" &&
          contactFormData[field.fieldKey] &&
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(
            contactFormData[field.fieldKey],
          )
        ) {
          setContactFormErrors((prev) => ({
            ...prev,
            [field.fieldKey]: {
              error: true,
              message: intl.formatMessage({ id: "invalid-email-address" }),
            },
          }));
          hasErrors = true;
        }

        // Phone validation for custom phone fields
        if (field.fieldType === "phone" && contactFormData[field.fieldKey]) {
          const countryHint =
            getPhoneCountry(
              contactFormData[field.fieldKey],
              contactFormData[`${field.fieldKey}Country`],
            ) ||
            contactFormData[`${field.fieldKey}Country`] ||
            getCompanyCountryCode();
          if (
            !isValidEmployeePhone(contactFormData[field.fieldKey], countryHint)
          ) {
            setContactFormErrors((prev) => ({
              ...prev,
              [field.fieldKey]: {
                error: true,
                message: getPhoneValidationMessage(
                  intl,
                  countryHint,
                  contactFormData[field.fieldKey],
                ),
              },
            }));
            hasErrors = true;
          }
        }

        // Number validation
        if (
          field.fieldType === "number" &&
          contactFormData[field.fieldKey] &&
          !/^\d+$/.test(contactFormData[field.fieldKey])
        ) {
          setContactFormErrors((prev) => ({
            ...prev,
            [field.fieldKey]: {
              error: true,
              message: `${intl.formatMessage({
                id: "please-enter-only-digits-for",
              })} ${field.fieldName.toLowerCase()}`,
            },
          }));
          hasErrors = true;
        }
      });
    }

    return !hasErrors;
  };

  const [customValuesErrors, setCustomValuesErrors] = useState({});

  const validateCompetitionCustomValues = () => {
    const customFields = survey_without_login?.customValues;
    let hasErrors = false;
    if (
      survey_without_login?.competitionInformation &&
      customFields?.fields?.length > 0
    ) {
      // Validate custom values
      customFields?.fields?.forEach((item) => {
        if (item.isRequired && !customValues[item.fieldKey]) {
          setCustomValuesErrors((prev) => ({
            ...prev,
            [item.fieldKey]: {
              error: true,
              message: `${intl.formatMessage({
                id: "please-enter",
              })} ${item.fieldName.toLowerCase()}`,
            },
          }));
          hasErrors = true;
        } else if (
          item.fieldType === "email" &&
          customValues[item.fieldKey] &&
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(
            customValues[item.fieldKey],
          )
        ) {
          setCustomValuesErrors((prev) => ({
            ...prev,
            [item.fieldKey]: {
              error: true,
              message: intl.formatMessage({
                id: "invalid-email-address",
              }),
            },
          }));
          hasErrors = true;
        } else if (
          item.fieldType === "number" &&
          customValues[item.fieldKey] &&
          !/^\d+$/.test(customValues[item.fieldKey])
        ) {
          setCustomValuesErrors((prev) => ({
            ...prev,
            [item.fieldKey]: {
              error: true,
              message: `${intl.formatMessage({
                id: "please-enter-only-digits-for",
              })} ${item.fieldName.toLowerCase()}`,
            },
          }));
          hasErrors = true;
        } else if (item.fieldType === "phone" && customValues[item.fieldKey]) {
          const countryHint =
            getPhoneCountry(
              customValues[item.fieldKey],
              customValues[`${item.fieldKey}Country`],
            ) ||
            customValues[`${item.fieldKey}Country`] ||
            getCompanyCountryCode();
          if (!isValidEmployeePhone(customValues[item.fieldKey], countryHint)) {
            setCustomValuesErrors((prev) => ({
              ...prev,
              [item.fieldKey]: {
                error: true,
                message: getPhoneValidationMessage(
                  intl,
                  countryHint,
                  customValues[item.fieldKey],
                ),
              },
            }));
            hasErrors = true;
          }
        }
      });
    }
    return hasErrors;
  };

  // Helper function to get hide questions from a single question/answer
  const getHideQuestionsFromAnswer = (question, answerValue) => {
    if (!answerValue) return [];

    const hideQues = [];

    if (["radio", "rating", "emoji"].includes(question.questionType)) {
      const selectedOption = question.options?.find(
        (opt) => opt?._id === answerValue,
      );
      if (selectedOption?.hide_questions) {
        hideQues.push(...selectedOption.hide_questions);
      }
    } else if (
      ["multiselect", "sequence", "skills"].includes(question.questionType)
    ) {
      const selectedOptions = question.options?.filter((opt) =>
        answerValue?.includes(opt?._id),
      );
      selectedOptions?.forEach((opt) => {
        if (opt.hide_questions) hideQues.push(...opt.hide_questions);
      });
    } else if (question.questionType === "gridspot") {
      // Gridspot answers are ObjectId[]. Radio uses the first id; multiselect
      // unions hide_questions from every selected spot (same as API).
      // Compare as strings — API/Redux ids can mix ObjectId-shaped values.
      const selectedIds = Array.isArray(answerValue)
        ? answerValue
        : [answerValue];
      const idMatches = (optId, selectedId) =>
        String(optId) === String(selectedId);
      if (question.gridInputType === "multiselect") {
        const selectedOptions = question.options?.filter((opt) =>
          selectedIds?.some((id) => idMatches(opt?._id, id)),
        );
        selectedOptions?.forEach((opt) => {
          if (opt.hide_questions) hideQues.push(...opt.hide_questions);
        });
      } else {
        const selectedOption = question.options?.find((opt) =>
          idMatches(opt?._id, selectedIds[0]),
        );
        if (selectedOption?.hide_questions) {
          hideQues.push(...selectedOption.hide_questions);
        }
      }
    } else if (question.questionType === "grid") {
      const gridHideQuestions = getGridHideQuestions(answerValue, question._id);
      hideQues.push(...gridHideQuestions);
    }

    return hideQues;
  };

  // Calculate which questions should be hidden based on current answers
  const getHiddenQuestionIds = () => {
    let hideQues = [];
    const answeresCopy = answeres;
    const waitTimeOptId = "6909f6d55fc3f8e6e5665337"; // Option Id of (Queue/Wait times) option // DELETE ME : once the show question logic is implemented, then remove this condition
    const dependentQuestionId = "6901e547863588f134b25c65"; // Question Id of (How long did you wait in line?) question // DELETE ME : once the show question logic is implemented, then remove this condition
    const indexOfQuestion = allQuestionsOriginal.findIndex(
      (question) => question._id === dependentQuestionId,
    );

    for (let index = 0; index < allQuestionsOriginal?.length; index++) {
      const question = allQuestionsOriginal[index];
      const answer = answeresCopy?.[question?._id];

      // Use helper function
      const hideIndices = getHideQuestionsFromAnswer(question, answer?.value);
      hideQues.push(...hideIndices);
      // DELETE ME : once the show question logic is implemented, then remove this condition
      if (
        answer?.value &&
        (Array.isArray(answer.value) || typeof answer.value === "string") &&
        answer.value.includes(waitTimeOptId) &&
        normalizeQuestionIndices(hideQues).includes(indexOfQuestion)
      ) {
        hideQues = hideQues.filter(
          (index) => toQuestionIndex(index) !== indexOfQuestion,
        );
      }
    }

    // Convert indices to question IDs (API may send string digits)
    return normalizeQuestionIndices(hideQues)
      .map((index) => allQuestionsOriginal[index]?._id)
      .filter(Boolean);
  };

  // Function to save partial responses for the current page
  const savePartialAnswers = async () => {
    try {
      return await savePartialResponse({
        allQuestions,
        trimStart,
        trimEnd,
        getHiddenQuestionIds,
        store,
        contactDetails,
        customValues,
        contactFormData,
        guest_info,
        visitDate,
        visitTimeIn,
        selectedTimeOut,
        survey_without_login,
        showContactForm,
        directorMessage,
      });
    } catch (error) {
      console.log(error);
    }
  };

  // Partial survey exit: save current state and close — no question/contact validation
  const handleSaveAndExit = async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    try {
      const result = await savePartialResponseOnExit({
        allQuestions,
        getHiddenQuestionIds,
        store,
        contactDetails,
        customValues,
        contactFormData,
        guest_info,
        visitDate,
        visitTimeIn,
        selectedTimeOut,
        survey_without_login,
        showContactForm,
        directorMessage,
      });
      if (result?.success) {
        attemptCloseTabOrRedirect(history, {
          surveyWithoutLogin: true,
          partialExit: true,
        });
        return;
      }
      const csatCode = csatErrorCode(result?.error);
      if (csatCode === CSAT_ERROR_CODES.ALREADY_SUBMITTED) {
        // This CSAT email was already answered (another tab or device).
        history.push("/sameDaySurvey");
        return;
      }
      toast.error(
        intl.formatMessage({
          id:
            csatCode === CSAT_ERROR_CODES.TOKEN_INVALID
              ? "csat-link-invalid"
              : "failed-to-save-and-exit",
        }),
      );
      setLoading(false);
    } catch (error) {
      console.log(error);
      toast.error(intl.formatMessage({ id: "failed-to-save-and-exit" }));
      setLoading(false);
    }
  };

  // Add a ref to store the timeout ID
  const autoCloseTimeoutRef = useRef(null);

  const handleClosePage = () => {
    // Clear the timeout when user manually closes
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
    setShowErrorPopup({ show: false });
  };

  useEffect(() => {
    if (showErrorPopup?.show) {
      // Store the timeout ID
      autoCloseTimeoutRef.current = setTimeout(() => {
        window.close();
      }, 5000);
    }

    // Cleanup function - clear timeout when component unmounts or effect re-runs
    return () => {
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }
    };
  }, [showErrorPopup]);

  const submitAnswers = async (initiatedBy = "") => {
    // Consolidating response data from the contact details
    let responseData = {
      customer_name: contactDetails?.name,
      customer_email: contactDetails?.email,
      customer_phone: contactDetails?.phone,
    };

    // If submission is from idle, collect all errors and show a popup
    if (initiatedBy === "idle") {
      const errors = [];

      if (isHelpDesk) {
        errors.push(
          intl.formatMessage({
            id: "please-complete-all-required-contact-information",
          }),
        );
      }

      const isContactFormValid = validateContactForm();
      if (!isContactFormValid) {
        errors.push(
          intl.formatMessage({
            id: "please-complete-all-required-contact-information",
          }),
        );
      }

      const hasErrors = validateCompetitionCustomValues();
      if (hasErrors) {
        errors.push(
          intl.formatMessage({ id: "please-correct-the-custom-field-values" }),
        );
      }

      // Check answers early when from idle
      let answeresCopy = await { ...answeres };
      // Remove empty or null values from the answersCopy object
      answeresCopy = removeEmptyOrNullValuesFromAnswersCopy(answeresCopy);

      if (!Object.keys(answeresCopy)?.length) {
        errors.push(
          intl.formatMessage({
            id: "you-must-provide-at-least-one-answer-before-submitting-the-survey",
          }),
        );
      }

      // If there are any errors, show popup and return
      if (errors?.length > 0) {
        // Show a modal/popup with all error messages
        setShowErrorPopup({
          title: intl.formatMessage({ id: "survey-cannot-be-submitted" }),
          errors: errors,
          show: true,
        });
        setLoading(false);
        return;
      }
    }

    const isContactFormValid = validateContactForm();
    if (!isContactFormValid) {
      setLoading(false);
      return;
    }

    const hasErrors = validateCompetitionCustomValues();
    if (hasErrors) {
      setLoading(false);
      return;
    }

    const formattedData = Object.entries(customValues).map(([key, value]) => ({
      fieldKey: key,
      value: value,
    }));

    setLoading(true); // Sets loading state to true at the start of the function
    let filesArray = [];

    // Destructuring survey data including answers and contact details
    let answeresCopy = await { ...answeres };

    // Loop through answers to find and process file type questions
    let isAllowGoogleQR = false;
    for (let item in answeresCopy) {
      const question = allQuestions.find((question) => question._id === item);
      if (question !== undefined) {
        if ("isFile" in answeresCopy[item]) {
          answeresCopy[item].questionType =
            question?.questionType === "handwrittenInput"
              ? "base64"
              : question?.questionType;
          filesArray.push({ [item]: answeresCopy[item] });
        }
        // Format phone numbers: E.164 value + dial code + ISO country
        if (
          question.questionType === "text" &&
          question.textType === "phone" &&
          answeresCopy[item].value !== ""
        ) {
          const formatted = formatPhoneAnswerForPayload(answeresCopy[item]);
          if (formatted) {
            answeresCopy[item] = formatted;
          }
        }
        // Remove empty or null values from the answersCopy object
        if (
          !answeresCopy[item]?.value ||
          (Array.isArray(answeresCopy[item].value) &&
            answeresCopy[item].value.length === 0)
        ) {
          delete answeresCopy[item];
        }
        if (
          answeresCopy[item]?.value &&
          question?.options?.find(
            (opt) =>
              opt?._id?.toString() === answeresCopy[item].value?.toString(),
          )?.allow_google_review
        ) {
          isAllowGoogleQR = true;
        }
      }
    }
    // Check if there are no answers provided

    if (!Object.keys(answeresCopy)?.length) {
      setLoading(false);
      toast.error(
        intl.formatMessage({
          id: "you-must-provide-at-least-one-answer-before-submitting-the-survey",
        }),
      );
      return;
    }
    let surveyTotal = 0;
    for (let index = 0; index < allQuestions.length; index++) {
      const question = allQuestions[index];
      if (!allQuestions[index]?.skip) {
        surveyTotal += question?.maxScore ? question?.maxScore : 0;
      }
    }
    const hiddenQuestionIds = await getHiddenQuestionIds();

    // Remove hidden question answers
    hiddenQuestionIds?.length > 0 &&
      hiddenQuestionIds?.forEach((questionId) => {
        delete answeresCopy[questionId];
      });

    // Preparing metadata for the survey response
    const metaData = {
      survey_id: localStorage.getItem("survey") || "",
      location_id: localStorage.getItem("location") || "",
      response_user_id: localStorage.getItem("user_id") || "",
      tag_id: localStorage.getItem("tag_id")
        ? localStorage.getItem("tag_id")
        : localStorage.getItem("tag"),
      guest_info,
    };
    withCsatToken(metaData); // CSAT email link: which email this answers
    if (survey_without_login?.visit_information) {
      metaData.visitDate = visitDate;
      metaData.visitTimeIn = visitTimeIn;
      metaData.visitTimeOut = selectedTimeOut
        ? moment(selectedTimeOut).format("HH:mm")
        : null;
    }
    if (survey_without_login?.guest_info) {
      metaData.guest_info = guest_info;
    }

    // Handling audit and email data if available
    const audit = localStorage.getItem("audit") ?? null;
    const email = localStorage.getItem("email") ?? null;
    if (audit) {
      metaData.audit = audit;
      metaData.email = email;
    }

    // Add Login user detail
    const userEmail = localStorage.getItem("user_email") ?? null;
    if (userEmail) {
      metaData.email = userEmail;
    }

    // Add creator_id if available in localstorage
    const ticketCreatorId = localStorage.getItem("creator_id") ?? null;

    // Add call center flag if available in localstorage
    const callcenter = localStorage.getItem("callcenter") ?? null;
    if (callcenter === "true") {
      responseData.call_center = true;
    }

    // Add source if available in localstorage
    const source = localStorage.getItem("source") ?? null;

    const override_id = localStorage.getItem("override_id") ?? null;
    if (override_id) {
      metaData.override_id = override_id;
    }

    const task_id = localStorage.getItem("task_id") ?? null;
    if (task_id) {
      metaData.task_id = task_id;
    }

    const is_override = localStorage.getItem("is_override") ?? null;
    if (is_override) {
      metaData.is_override = is_override;
    }

    const override_date = localStorage.getItem("override_date") ?? null;
    if (override_date) {
      metaData.override_date = override_date;
    }

    const response_user_id = localStorage.getItem("response_user_id") ?? null;
    if (response_user_id) {
      metaData.response_user_id = response_user_id;
    }

    // Add custom values to the metadata
    if (formattedData?.length > 0) {
      metaData.customValues = formattedData;
    }

    if (showContactForm && Object.keys(contactFormData).length > 0) {
      const contactInfo = {
        name: contactFormData.name || "",
        email: contactFormData.email || "",
        phone: contactFormData.phone || "",
        country:
          contactFormData.phoneCountry ||
          getPhoneCountry(contactFormData.phone) ||
          undefined,
        code:
          getPhoneDialCode(
            contactFormData.phone,
            contactFormData.phoneCountry,
          ) || undefined,
        customValues: [],
      };

      if (survey_without_login?.contact_custom_fields?.length > 0) {
        survey_without_login.contact_custom_fields.forEach((field) => {
          if (contactFormData[field.fieldKey]) {
            contactInfo.customValues.push({
              fieldKey: field.fieldKey,
              fieldName: field.fieldName,
              fieldType: field.fieldType,
              value: contactFormData[field.fieldKey],
            });
          }
        });
      }

      metaData.contactInfo = contactInfo;

      if (survey_without_login?.helpdesk_create_ticket) {
        metaData.helpdesk_create_ticket = true;
      }
    }

    // Creating a FormData object for sending the survey response
    const form_data = new FormData();
    form_data.append("metaData", JSON.stringify(metaData));
    form_data.append("jsonData", JSON.stringify(answeresCopy));
    form_data.append("responseData", JSON.stringify(responseData));
    form_data.append("director_message", directorMessage);
    form_data.append("ticketCreatorId", ticketCreatorId);
    form_data.append("surveyTotal", surveyTotal);
    form_data.append("source", source);

    // Add company id to the FormaData
    const companyId = localStorage.getItem("company_id");
    form_data.append("company_id", companyId);
    if (
      localStorage.getItem("tmp_response_id") &&
      localStorage.getItem("tmp_response_id") !== "undefined"
    ) {
      form_data.append(
        "tmp_response_id",
        localStorage.getItem("tmp_response_id"),
      );
    }

    // Adding files to the FormData if any exist
    if (filesArray.length) {
      filesArray.forEach((item) => {
        const key = Object.keys(item)[0];
        const { questionType } = item[key];
        let images = [];

        // Processing image files based on question type
        if (questionType === "file" || questionType === "handwrittenInput") {
          if (item[key]?.value?.length) {
            item[key].value.forEach((img) => images.push(img));
          }
          if (item[key]?.image?.length) {
            item[key].image.forEach((img) => images.push(img));
          }
        } else {
          images = item[key]?.image || [];
        }

        // Appending images to formData
        if (images.length) {
          images.forEach((image) => {
            form_data.append(key, image);
          });
        }
      });
    }

    try {
      /**
       *  DONOT DELETE
       *  This code is to add validation to allow one survey per browser
       */
      // const surveyCookie = cookies.survey;
      // if (surveyCookie?.survey_id !== localStorage.getItem("survey")) {
      // Making an API call to submit the survey answers
      const result = await axiosPostData("survey/answerSurvey", form_data);

      // On successful submission, redirect to thank you page
      if (result) {
        setCookie(
          "survey",
          {
            survey: "submited",
            survey_id: localStorage.getItem("survey") || "",
          },
          { path: "/", maxAge: 86400 },
        );

        // Check if response expiration is enabled
        // Not for a CSAT email: the server allows one answer per email, and
        // this cookie would block the customer's other emails for the survey.
        if (
          survey_without_login?.multipleSubmissionsDisabled &&
          !getCsatToken()
        ) {
          const expirationSeconds = calculateExpirationTime(
            survey_without_login.expirationTimeUnit,
            survey_without_login.expirationDuration,
          );

          // Set cookie with calculated expiration
          setSurveyResponseCookie(
            "survey_response",
            {
              survey: "submited",
              survey_id: localStorage.getItem("survey") || "",
              expires: expirationSeconds,
            },
            {
              path: "/",
              maxAge: expirationSeconds,
            },
          );
        }

        if (surveyWithoutLogin) {
          localStorage.removeItem("location");
          history.push({
            pathname: "/surveyThankYou",
            state: {
              surveyWithoutLogin: true,
              uniqueshortcode: result?.data?.results?.uniqueshortcode || "",
              isAllowGoogleQR: isAllowGoogleQR,
            },
          });
        } else {
          history.push({
            pathname: "/surveyThankYou",
            state: {
              uniqueshortcode: result?.data?.results?.uniqueshortcode || "",
              isAllowGoogleQR: isAllowGoogleQR,
            },
          });
        }

        // setQuestionPerPage()
      }

      /**
       *  DONOT DELETE
       *  This code is to add validation to allow one survey per browser
       */
      // } else {
      //   history.push("/sameDaySurvey");
      // }
    } catch (e) {
      // Handling errors during submission
      console.log(e);
      const csatCode = csatErrorCode(e);
      if (csatCode === CSAT_ERROR_CODES.ALREADY_SUBMITTED) {
        // This CSAT email was already answered (another tab, a double tap).
        history.push("/sameDaySurvey");
      } else {
        // Show error toast to user for network/server errors
        toast.error(
          intl.formatMessage({
            id:
              csatCode === CSAT_ERROR_CODES.TOKEN_INVALID
                ? "csat-link-invalid"
                : "network-error-please-try-again",
          }),
        );
      }
    }
    setLoading(false); // Sets loading state to false after function execution
  };

  const notAnsweredWriteInOption = () => {
    // eslint-disable-next-line array-callback-return
    const answeresCopy = store.getState().survey.answeres;
    return allQuestions.slice(trimStart, trimEnd + 1).find((e) => {
      if (e.questionType === "gridspot") {
        return false;
      }
      if (e._id in answeresCopy) {
        const answerArr = Array.isArray(answeresCopy[e._id].value)
          ? answeresCopy[e._id].value
          : [answeresCopy[e._id].value];
        if (answeresCopy[e._id].value && answerArr.length > 0) {
          const selectedOptions =
            allQuestions
              ?.find((que) => que?._id === e._id)
              ?.options?.filter((opt) => answerArr?.includes(opt?._id)) || [];
          if (selectedOptions?.some((opt) => opt?.write_in)) {
            const writeIn = answeresCopy[e._id]?.write_in?.trim();
            if (!writeIn || writeIn.length === 0) {
              return true;
            }
          }
        }
      }
    });
  };

  const imageRequiredOption = () => {
    // eslint-disable-next-line array-callback-return
    const answeresCopy = store.getState().survey.answeres;
    return allQuestions.slice(trimStart, trimEnd + 1).find((e) => {
      if (e.questionType === "gridspot") {
        return false;
      }
      if (e._id in answeresCopy) {
        const answerArr = Array.isArray(answeresCopy[e._id].value)
          ? answeresCopy[e._id].value
          : [answeresCopy[e._id].value];
        if (answeresCopy[e._id].value && answerArr.length > 0) {
          const selectedOptions =
            allQuestions
              ?.find((que) => que?._id === e._id)
              ?.options?.filter((opt) => answerArr?.includes(opt?._id)) || [];
          if (selectedOptions?.some((opt) => opt?.image_required)) {
            const imageData = answeresCopy[e._id]?.image?.length;
            if (!imageData || imageData === 0) {
              return true;
            }
          }
        }
      }
    });
  };

  const notAnsweredQ = () => {
    // if (isSubmitPage) return undefined;
    // eslint-disable-next-line array-callback-return
    const answeresCopy = store.getState().survey.answeres;
    return allQuestions.slice(trimStart, trimEnd + 1).find((e) => {
      if (e.required && !e?.skip && e.questionType !== "grid") {
        if (e.questionType === "gridspot" && getGridspotConfigurationError(e)) {
          return true;
        }
        if (e._id in answeresCopy) {
          if (!answeresCopy[e._id].value?.toString()?.length) {
            return true;
          }
        } else {
          return true;
        }
      }
    });
  };

  const minOptionChecked = () => {
    return allQuestions.slice(trimStart, trimEnd + 1).find((item) => {
      let bool; // Variable to store whether the condition is true
      if (item.questionType === "sequence" && !item?.skip) {
        // Check if min_options is defined and greater than 0
        if (item.min_options && item.min_options > 0) {
          // Check if the answered options are less than the minimum required
          if (answeres[item._id]?.value?.length < item?.min_options) {
            bool = true; // If the condition is true, set bool to true
          }
        }
      }
      if (item.questionType === "multiselect" && !item?.skip) {
        // Check if min_options is defined and greater than 0
        if (item.min_options && item.min_options > 0) {
          // Check if the answered options are less than the minimum required
          if (answeres[item._id]?.value?.length < item?.min_options) {
            bool = true; // If the condition is true, set bool to true
          }
        }
      }
      return bool;
    });
  };

  const maxOptionChecked = () => {
    return allQuestions.slice(trimStart, trimEnd + 1).find((item) => {
      let bool; // Variable to store whether the condition is true
      if (item.questionType === "sequence" && !item?.skip) {
        // Check if max_options is defined and greater than 0
        if (item.max_options && item.max_options > 0) {
          // Check if the answered options are more than the maximum allowed
          if (answeres[item._id]?.value?.length > item?.max_options) {
            bool = true; // If the condition is true, set bool to true
          }
        }
      }
      if (item.questionType === "multiselect" && !item?.skip) {
        // Check if max_options is defined and greater than 0
        if (item.max_options && item.max_options > 0) {
          // Check if the answered options are more than the maximum allowed
          if (answeres[item._id]?.value?.length > item?.max_options) {
            bool = true; // If the condition is true, set bool to true
          }
        }
      }
      return bool;
    });
  };

  const minCharacterChecked = () => {
    return allQuestions.slice(trimStart, trimEnd + 1).find((item) => {
      let bool;
      // Check if the question type is 'text' and textType is 'textbox'
      if (item.questionType === "text" && item?.textType === "textbox") {
        // Check if minimum_character validation is defined and greater than 0
        if (item.minimum_character && item.minimum_character > 0) {
          // Check if the question has an answer
          if (item._id in answeres) {
            // Check if the length of the trimmed answer is less than the minimum required characters
            if (
              answeres[item._id].value &&
              answeres[item._id].value?.trim()?.length < item?.minimum_character
            ) {
              bool = true; // If the condition is true, set bool to true
            }
          }
        }
      }
      return bool;
    });
  };

  const emailTypeChecked = () => {
    // Regular expression pattern to check for valid email addresses
    var pattern = new RegExp(
      /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i,
    );
    // Validates multiple emails separated by comma; space allowed only after comma
    const areEmailsValid = (value) => {
      const emails = value.split(",").map((s) => s.trim());
      return emails.every((email) => email.length > 0 && pattern.test(email));
    };
    return allQuestions.slice(trimStart, trimEnd + 1).find((item) => {
      let bool; // Variable to hold the boolean result of validation
      // Check if the question is of type 'text' and subtype 'email'
      if (item.questionType === "text" && item?.textType === "email") {
        // Check if the question has an answer in the 'answeres' object
        if (item._id in answeres) {
          // Check if the answer value exists and is not empty, and does not match the email pattern(s)
          if (
            answeres[item._id].value &&
            answeres[item._id].value.trim().length > 0 &&
            !areEmailsValid(answeres[item._id].value)
          ) {
            bool = true; // Set bool to true if validation fails
          }
        }
      }
      return bool;
    });
  };

  const phoneTypeChecked = () => {
    return allQuestions.slice(trimStart, trimEnd + 1).find((item) => {
      let bool;
      // Check if the question is of type 'text' and subtype 'phone'
      if (item.questionType === "text" && item?.textType === "phone") {
        // Check if the question has an answer in the 'answeres' object
        if (item._id in answeres) {
          const phoneValue = answeres[item._id].value;
          if (
            phoneValue &&
            phoneValue.toString().trim().length > 0 &&
            !isValidEmployeePhone(
              phoneValue,
              answeres[item._id].phoneCountry || getCompanyCountryCode(),
            )
          ) {
            bool = true; // Set bool to true if validation fails
          }
        }
      }
      return bool;
    });
  };

  const numberTypeChecked = () => {
    return allQuestions.slice(trimStart, trimEnd + 1).find((item) => {
      let bool = false;
      // Check if the question is of type 'number'
      if (item.questionType === "number") {
        // Check if max_number_value validation is defined and greater than 0
        if (item.max_number_value && Number(item.max_number_value) > 0) {
          // Check if the question has an answer in the 'answeres' object
          if (item._id in answeres) {
            // Check if the answer value exists and is greater than the maximum allowed number value
            if (
              answeres[item._id].value &&
              answeres[item._id].value.toString().trim().length > 0 &&
              Number(answeres[item._id]?.value) > Number(item.max_number_value)
            ) {
              bool = true;
            }
          }
        }
      }

      return bool;
    });
  };

  const gridTypeChecked = () => {
    return allQuestions.slice(trimStart, trimEnd + 1).find((item) => {
      let bool = false;
      // Check if the question is of type 'grid' and is required
      if (item.questionType === "grid" && item.required) {
        // Check if the question has an answer in the 'answeres' object
        if (item._id in answeres && answeres[item._id].value) {
          const gridAnswers = answeres[item._id].value;
          const isMultiselect = item.gridInputType === "multiselect";

          for (const row of item.gridRows) {
            const rowAnswer = gridAnswers[row._id];

            if (isMultiselect) {
              // For multiselect: check if row has at least one selection
              if (
                !rowAnswer ||
                !Array.isArray(rowAnswer) ||
                rowAnswer.length === 0
              ) {
                bool = true; // Validation fails - required row has no selections
                break;
              }
            } else {
              // For radio: check if row has a selection
              if (!rowAnswer) {
                bool = true; // Validation fails - required row has no selection
                break;
              }
            }
          }
        } else {
          // No answer exists for a required grid question
          bool = true;
        }
      }

      return bool;
    });
  };

  // Initialize state variables to store error messages
  const [error, setError] = useState(false);
  const [minError, setMinError] = useState();
  const [maxError, setMaxError] = useState();
  const [minCharError, setMinCharError] = useState();
  const [emailError, setEmailError] = useState();
  const [phoneError, setPhoneError] = useState();
  const [numberError, setNumberError] = useState();
  const [gridError, setGridError] = useState();
  const [writeInError, setWriteInError] = useState();
  const [imageRequiredError, setImageRequiredError] = useState();
  const [visitTimeError, setVisitTimeError] = useState(false);

  // Effect hook to handle validation of various question types when answers change
  useEffect(() => {
    // Retrieve questions that need validation
    const notAnswered = notAnsweredQ();
    const minOptionCheck = minOptionChecked();
    const maxOptionCheck = maxOptionChecked();
    const minCharCheck = minCharacterChecked();
    const emailChecked = emailTypeChecked();
    const phoneChecked = phoneTypeChecked();
    const numberChecked = numberTypeChecked();

    // Clear error states if validation passes for corresponding questions
    if (minOptionCheck?._id !== minError?._id) {
      setMinError(null);
    }

    if (maxOptionCheck?._id !== maxError?._id) {
      setMaxError(null);
    }

    if (notAnswered?._id !== error?._id) {
      setError(null);
    }
    if (minCharCheck?._id !== minCharError?._id) {
      setMinCharError(null);
    }
    if (emailChecked?._id !== emailError?._id) {
      setEmailError(null);
    }
    if (phoneChecked?._id !== phoneError?._id) {
      setPhoneError(null);
    }
    // Set number error state if validation fails, otherwise clear it
    if (numberChecked) {
      setNumberError(numberChecked);
    } else {
      setNumberError(null);
    }

    setWriteInError(null);
    setImageRequiredError(null);
  }, [answeres]); // Run the effect whenever 'answeres' state changes

  useEffect(() => {
    const isContactRequired = checkContactInfoRequired();

    if (isContactRequired) {
      setShowContactForm(isContactRequired);
    } else {
      setShowContactForm(false);
    }
  }, [answeres]);

  const myRef = useRef(null);
  useEffect(() => {
    myRef?.current?.scrollIntoView();
  }, [error]);

  useEffect(() => {
    const fetchData = async () => {
      clearAnsweres_();
      const location = localStorage.getItem("location");
      const logoLocal = localStorage.getItem("logo");
      if (logoLocal) {
        setLogo(logoLocal);
      }
      if (location && !isMobile) {
        setEmpLoading(true);
        const detail = await getDetails(location, "forEmp");
        if (detail) {
          localStorage.setItem("employees", JSON.stringify(detail?.employee));
        }
        setEmpLoading(false);
      }
    };

    fetchData();
  }, []);

  const checkContactInfoRequired = (
    selectedOptionId = "",
    selectedQuestionID = "",
  ) => {
    const answeresCopy = answeres;
    let isContactRequired = false;

    for (let questionId in answeresCopy) {
      const question = allQuestionsOriginal.find((q) => q._id === questionId);

      if (
        question?.questionType === "radio" &&
        answeresCopy[questionId]?.value
      ) {
        const selectedOptionId = answeresCopy[questionId].value;
        const selectedOption = question.options?.find(
          (opt) => opt._id === selectedOptionId,
        );

        if (
          selectedOption?.contact_info_required === true &&
          survey_without_login?.contact_info_required === true
        ) {
          isContactRequired = true;
          break;
        }
      }
    }
    if (selectedOptionId && selectedQuestionID) {
      const question = allQuestionsOriginal.find(
        (q) => q._id === selectedQuestionID,
      );
      const selectedOption = question.options?.find(
        (opt) => opt._id === selectedOptionId,
      );

      if (
        selectedOption?.contact_info_required === true &&
        survey_without_login?.contact_info_required === true
      ) {
        isContactRequired = true;
      } else {
        isContactRequired = false;
      }
    }

    return isContactRequired;
  };

  const onShowNextQuestion = () => {
    // Only redirect to next page if there's one question per page and the current question is not last question
    if (qPerPage === 1 && page !== allQuestions.length) {
      setGs(false);
      setPage(page + 1);
      setDirection(1);
    }
  };

  const contidionalAutoNext = async (
    routeToIndex,
    question,
    indexToHide,
    selectedId,
    questionID,
  ) => {
    let currentQuestionIndex = "";
    let calculatedTotalPages = 0;
    // APIs may send route_to_index / hide_questions as string digits
    const parsedRouteToIndex = toQuestionIndex(routeToIndex);
    const hideIndexList = normalizeQuestionIndices(indexToHide);
    // Use original list so hide_questions / route_to_index indices stay correct
    // even when schedule_windows (or hide) removed questions from the visible list.
    for (let index = 0; index < allQuestionsOriginal.length; index++) {
      const element = allQuestionsOriginal[index]._id === questionID;
      if (element) {
        currentQuestionIndex = index;
      }
    }

    // Create a new array of questions with the "skip" property set to true for certain questions
    let lastIndex = allQuestions.length - 1;
    const routeSkipIds = new Set();
    allQuestionsOriginal.forEach((item, index) => {
      if (
        parsedRouteToIndex != null &&
        index < parsedRouteToIndex &&
        index > currentQuestionIndex
      ) {
        routeSkipIds.add(item._id);
      }
    });
    const greyOutQestion = allQuestions.map((item) => {
      if (routeSkipIds.has(item._id)) {
        // Add empty answer and mark the question as skipped
        addAnswer_({
          id: item._id,
          value: "",
          type: "id",
        });
        removeAnswer_({ id: item._id });
        return { ...item, skip: true };
      }
      const originalIndex = allQuestionsOriginal.findIndex(
        (q) => q._id === item._id,
      );
      if (originalIndex > currentQuestionIndex) {
        return { ...item, skip: false };
      }
      return { ...item, skip: item.skip ?? false };
    });
    // Set state with the updated greyed-out questions
    setAllQuestions(greyOutQestion);

    // Create a new array of original questions with the "skip" property set to true for certain questions
    const allGreyOutQestion = allQuestionsOriginal.map((item, index) => {
      if (
        parsedRouteToIndex != null &&
        index < parsedRouteToIndex &&
        index > currentQuestionIndex
      ) {
        addAnswer_({
          id: item._id,
          value: "",
          type: "id",
        });

        return { ...item, skip: true };
      } else {
        if (index > currentQuestionIndex) {
          return { ...item, skip: false };
        } else {
          return { ...item, skip: item.skip ?? false };
        }
      }
    });

    // Set state with the updated greyed-out questions in the original question set
    setAllQuestionsOriginal(allGreyOutQestion);

    const hideQues = []; // Initialize an empty array to store the questions to be hidden
    const waitTimeOptId = "6909f6d55fc3f8e6e5665337"; // Option Id of (Queue/Wait times) option
    const dependentQuestionId = "6901e547863588f134b25c65"; // Question Id of (How long did you wait in line?) question
    const indexOfQuestion = allQuestionsOriginal.findIndex(
      (question) => question._id === dependentQuestionId,
    );

    // Logic to determine questions to be hidden based on selected options
    // if (indexToHide !== undefined && indexToHide?.length > 0) {
    const locationId = localStorage.getItem("location");
    // Create a copy of the answeres state
    const answeresCopy = await answeres;
    let isContactRequired = false;
    // Loop through the questions and find the ones that need to be hidden
    for (let index = 0; index < allQuestionsOriginal.length; index++) {
      // if (allQuestionsOriginal[index]._id in answeresCopy) {
      let prevAnswer = answeresCopy[allQuestionsOriginal[index]._id];
      // Determine the actual answer value to check
      // If this is the current question being edited, use the new selectedId
      // Otherwise use the previous answer value
      const answerToCheck =
        allQuestionsOriginal[index]._id === questionID
          ? selectedId
          : prevAnswer?.value;

      // Only check contact requirement if there's an answer
      if (
        answerToCheck &&
        allQuestionsOriginal[index]?.questionType === "radio"
      ) {
        const question = allQuestionsOriginal[index];
        const selectedOption = question.options?.find(
          (opt) => opt?._id === answerToCheck,
        );

        if (
          selectedOption?.contact_info_required === true &&
          survey_without_login?.contact_info_required === true
        ) {
          isContactRequired = true;
          // No need to continue checking once we find one
          // But we still need to process hide_questions, so don't break
        }
      }

      // Use helper function to get hidden questions
      const hideIndices = getHideQuestionsFromAnswer(
        allQuestionsOriginal[index],
        answerToCheck,
      );
      hideQues.push(...hideIndices);
    }
    // Combine indices to be hidden (normalize string digits from API)
    let allHiddenIndex = [
      ...normalizeQuestionIndices(hideQues),
      ...hideIndexList,
    ];
    /* DELETE ME : once the show question logic is implemented, then remove this condition */
    // if the selectedId includes the optionID, then remove the indexOfQuestion from allHiddenIndex
    if (
      selectedId?.includes(waitTimeOptId) &&
      allHiddenIndex.includes(indexOfQuestion)
    ) {
      allHiddenIndex = allHiddenIndex.filter(
        (index) => index !== indexOfQuestion,
      );
    }
    const hiddenIndexSet = new Set(allHiddenIndex);
    // Filter out questions to be hidden based on their indices (original array)
    let filteredQuestions = allGreyOutQestion?.filter(
      (_, i) => !hiddenIndexSet.has(i),
    );
    // Keep schedule_windows filtering on the visible list only
    filteredQuestions = filterQuestionsByScheduleWindows(filteredQuestions);

    /* Not remove answers for the hidden questions for now */
    // Remove answers for the hidden questions
    // let hiddenQuestions = allGreyOutQestion?.filter((que, i) =>
    //   indexToHide?.includes(i)
    // );
    // hiddenQuestions?.forEach((hiddenQue) =>
    //   removeAnswer_({ id: hiddenQue?._id })
    // );

    // If no location ID is available, filter out questions related to employees
    if (!locationId) {
      filteredQuestions = filteredQuestions.filter(
        (item) =>
          item.questionType !== "employees" &&
          item?.questionType !== "inventory",
      );
    }

    // Append additional questions based on conditions
    const allQues =
      survey_without_login?.leadershipUser ||
      survey_without_login?.visit_information ||
      survey_without_login?.competitionInformation
        ? [
            ...filteredQuestions,
            { questionType: "director_message", page_break: true },
          ]
        : [...filteredQuestions];
    // getPageBreaks(allQues, surveyType);
    const result = getPageBreaks(allQues, surveyType, isContactRequired);
    calculatedTotalPages = result.totalPages;
    // Update the state with the filtered questions
    setAllQuestions(allQues);
    lastIndex = allQues.length - 1;
    // }
    // Map original route_to_index → visible question index (schedule/hide aware)
    const resolveVisibleRouteTargetIndex = () => {
      let targetVisibleIndex = -1;
      for (
        let index = parsedRouteToIndex;
        index < allGreyOutQestion.length;
        index += 1
      ) {
        const targetQuestionId = allGreyOutQestion[index]?._id;
        targetVisibleIndex = allQues.findIndex(
          (q) => q._id === targetQuestionId,
        );
        if (targetVisibleIndex >= 0) {
          break;
        }
      }
      if (targetVisibleIndex < 0) {
        targetVisibleIndex = Math.max(allQues.length - 1, 0);
      }
      return targetVisibleIndex;
    };

    // Conditional logic for auto-navigation
    const currentVisibleIndex = allQues.findIndex((q) => q._id === questionID);
    const autoNextQuestion =
      allQuestionsOriginal[currentQuestionIndex];
    const isGridspotAutoNext =
      autoNextQuestion?.questionType === "gridspot" &&
      autoNextQuestion?.gridInputType !== "multiselect";
    if (
      qPerPage === 1 &&
      (["radio", "rating", "emoji"]?.includes(autoNextQuestion?.questionType) ||
        isGridspotAutoNext)
    ) {
      let notAnswered = notAnsweredQ();
      let minOptionCheck = minOptionChecked();
      let maxOptionCheck = maxOptionChecked();
      let minCharCheck = minCharacterChecked();
      let emailChecked = emailTypeChecked();
      let phoneChecked = phoneTypeChecked();
      let numberChecked = numberTypeChecked();
      let gridChecked = gridTypeChecked();
      let notWriteIN = notAnsweredWriteInOption();
      let imageRequired = imageRequiredOption();

      if (
        notAnswered === undefined &&
        minOptionCheck === undefined &&
        maxOptionCheck === undefined &&
        minCharCheck === undefined &&
        emailChecked === undefined &&
        phoneChecked === undefined &&
        numberChecked === undefined &&
        gridChecked === undefined &&
        notWriteIN === undefined &&
        imageRequired === undefined
      ) {
        if (parsedRouteToIndex != null) {
          savePartialAnswers();
          setPage(resolveVisibleRouteTargetIndex() + 1);
          setDirection(1);
        } else if (calculatedTotalPages !== page) {
          const isContactRequired = checkContactInfoRequired(
            selectedId,
            questionID,
          );
          // Check if this is the last question page (visible list)
          const isLastQuestion = currentVisibleIndex === allQues.length - 1;
          // Navigate to next page if:
          // 1. if it is not the last question
          if (!isLastQuestion) {
            savePartialAnswers();
            setGs(false);
            setPage(page + 1);
            setDirection(1);
          }
          // 2. if it is the last question and contact info is required (need to show contact form)
          if (isLastQuestion && isContactRequired) {
            savePartialAnswers();
            setGs(false);
            setPage(page + 1);
            setDirection(1);
          }
        }
      } else if (notAnswered) {
        setError(notAnswered);
        myRef?.current?.scrollIntoView();
      } else if (minOptionCheck) {
        setMinError(minOptionCheck);
        myRef?.current?.scrollIntoView();
      } else if (maxOptionCheck) {
        setMaxError(maxOptionCheck);
        myRef?.current?.scrollIntoView();
      } else if (minCharCheck) {
        setMinCharError(minCharCheck);
        myRef?.current?.scrollIntoView();
      } else if (emailChecked) {
        setEmailError(emailChecked);
        myRef?.current?.scrollIntoView();
      } else if (phoneChecked) {
        setPhoneError(phoneChecked);
        myRef?.current?.scrollIntoView();
      } else if (numberChecked) {
        setNumberError(numberChecked);
        myRef?.current?.scrollIntoView();
      } else if (gridChecked) {
        setGridError(gridChecked);
        myRef?.current?.scrollIntoView();
      } else if (notWriteIN) {
        setWriteInError(notWriteIN);
        myRef?.current?.scrollIntoView();
      } else if (imageRequired) {
        setImageRequiredError(imageRequired);
        myRef?.current?.scrollIntoView();
      }
    } else if (qPerPage !== 1) {
      if (parsedRouteToIndex != null) {
        // Keep prior multi-page behavior (usually stay on current page),
        // but compare against the visible-list index so schedule/hide
        // filtering does not misfire the last-question check.
        const targetVisibleIndex = resolveVisibleRouteTargetIndex();
        if (targetVisibleIndex === lastIndex) {
          setPage(page + 1);
          setDirection(1);
        } else {
          setPage(page);
          setDirection(1);
        }
      }
    }
  };

  /* 
  @param actualAnswers: The answers to the question
  @param questionID: The ID of the question
  @returns: An array of question IDs to hide
  @description: This function is used to get the questions to hide based on the answers to the question
  */
  const getGridHideQuestions = (actualAnswers, questionID) => {
    const hideQuestions = [];
    const question = allQuestionsOriginal.find((q) => q._id === questionID);
    if (actualAnswers && Object.keys(actualAnswers)?.length > 0) {
      Object.keys(actualAnswers)?.forEach((rowId) => {
        const selectedOptionId = actualAnswers[rowId];

        // Handle both single selection and multi-selection
        const selectedOptionIds = Array.isArray(selectedOptionId)
          ? selectedOptionId
          : [selectedOptionId];

        selectedOptionIds?.forEach((optionId) => {
          // Find the option that matches the selected optionId
          const selectedOption = question?.options?.find(
            (option) => option._id === optionId,
          );

          // Check if this option has grid_hide_questions array
          if (
            selectedOption &&
            selectedOption?.grid_hide_questions &&
            Array.isArray(selectedOption?.grid_hide_questions)
          ) {
            // Find the specific hide question configuration for this rowId
            const hideQuestionConfig = selectedOption.grid_hide_questions.find(
              (config) => config.rowId === rowId,
            );

            // If found, add the hide_questions for this rowId
            if (
              hideQuestionConfig &&
              hideQuestionConfig.hide_questions &&
              Array.isArray(hideQuestionConfig.hide_questions)
            ) {
              hideQuestions.push(...hideQuestionConfig.hide_questions);
            }
          }
        });
      });
    }
    return hideQuestions;
  };

  /* 
  @param actualAnswers: The answers to the question
  @param questionID: The ID of the question
  @returns: An array of question IDs to hide
  @description: This function is used to hide the questions
  */
  const gridHideQuestions = async (actualAnswers, questionID) => {
    const answeresCopy = await answeres;

    const hideQuestions = [];
    const gridHideQuestions = getGridHideQuestions(actualAnswers, questionID);

    // Loop through the questions and find the ones that need to be hidden
    for (let index = 0; index < allQuestions.length; index++) {
      let prevAnswer = answeresCopy[allQuestions[index]._id];
      if (
        allQuestions[index]?.questionType === "grid" &&
        prevAnswer &&
        Object.keys(prevAnswer?.value)?.length > 0 &&
        allQuestions[index]?._id !== questionID
      ) {
        Object.keys(prevAnswer?.value)?.forEach((rowId) => {
          const selectedOptionId = prevAnswer?.value[rowId];

          // Handle both single selection and multi-selection
          const selectedOptionIds = Array.isArray(selectedOptionId)
            ? selectedOptionId
            : [selectedOptionId];

          selectedOptionIds?.forEach((optionId) => {
            // Find the option that matches the selected optionId
            const selectedOption = allQuestions[index]?.options?.find(
              (option) => option._id === optionId,
            );

            // Check if this option has grid_hide_questions array
            if (
              selectedOption &&
              selectedOption?.grid_hide_questions &&
              Array.isArray(selectedOption?.grid_hide_questions)
            ) {
              // Find the specific hide question configuration for this rowId
              const hideQuestionConfig =
                selectedOption.grid_hide_questions.find(
                  (config) => config.rowId === rowId,
                );

              // If found, add the hide_questions for this rowId
              if (
                hideQuestionConfig &&
                hideQuestionConfig.hide_questions &&
                Array.isArray(hideQuestionConfig.hide_questions)
              ) {
                hideQuestions.push(...hideQuestionConfig.hide_questions);
              }
            }
          });
        });
      }
    }

    // Combine indices to be hidden
    const allHiddenIndex = [...hideQuestions, ...gridHideQuestions];
    // Remove duplicates from hide questions array (normalize string digits)
    const uniqueHideQuestions = [
      ...new Set(normalizeQuestionIndices(allHiddenIndex)),
    ];
    if (uniqueHideQuestions?.length > 0) {
      const hiddenIndexSet = new Set(uniqueHideQuestions);
      // Filter out questions to be hidden based on their indices
      let filteredQuestions = allQuestionsOriginal?.filter(
        (_, i) => !hiddenIndexSet.has(i),
      );
      filteredQuestions = filterQuestionsByScheduleWindows(filteredQuestions);

      // Remove answers for the hidden questions
      let hiddenQuestions = allQuestionsOriginal?.filter((que, i) =>
        hiddenIndexSet.has(i),
      );
      hiddenQuestions?.forEach((hiddenQue) =>
        removeAnswer_({ id: hiddenQue?._id }),
      );
      const locationId = localStorage.getItem("location");
      // If no location ID is available, filter out questions related to employees
      if (!locationId) {
        filteredQuestions = filteredQuestions.filter(
          (item) =>
            item.questionType !== "employees" &&
            item?.questionType !== "inventory",
        );
      }

      // Append additional questions based on conditions
      const allQues =
        survey_without_login?.leadershipUser ||
        survey_without_login?.visit_information ||
        survey_without_login?.competitionInformation
          ? [
              ...filteredQuestions,
              { questionType: "director_message", page_break: true },
            ]
          : [...filteredQuestions];
      getPageBreaks(allQues, surveyType, showContactForm);
      // Update the state with the filtered questions
      setAllQuestions(allQues);

      return uniqueHideQuestions;
    }
  };

  const [submitSurvey, setSubmitSurvey] = useState(false);
  useEffect(() => {
    if (submitSurvey) {
      submitAnswers();
    }
  }, [submitSurvey]);

  const finishSurvey = (shouldFinish, question) => {
    if (!shouldFinish) {
      return;
    }
    let currentQuestionIndex = "";
    // Finding the index of the current question in the array of all questions
    for (let index = 0; index < allQuestions.length; index++) {
      const element = allQuestions[index]._id === question._id;
      if (element) {
        currentQuestionIndex = index;
      }
    }
    for (
      let index = currentQuestionIndex + 1;
      index < allQuestions.length;
      index++
    ) {
      const questionId = allQuestions[index]._id;
      removeAnswer_({ id: questionId });
    }
    // Set submitSurvey state to true to indicate survey completion
    setSubmitSurvey(true);
  };

  const { getRemainingTime } = useIdleTimer({
    timeout: 1000 * (Number(survey_without_login?.ideal_duration) || 60),
    onIdle: async () => {
      getRemainingTime();
      // This is used to submit the answers when the user is idle for a certain period of time
      submitAnswers("idle");
    },

    debounce: 500,
  });
  const hasQuestionError = (err, qId) => Boolean(err && err._id === qId);

  return (
    <>
      {isHelpDesk ? (
        <div
          className={`${commonPageCss.bgPrimary} pb-5`}
          style={{ minHeight: "100vh" }}
        >
          <SupportLang />
          <div className={commonPageCss.topHeaderSurvery}>
            <img className={commonPageCss.topLogo} src={logo} alt="logo" />
          </div>
          <Paper
            className={`${commonPageCss.oneQuestionCardNarrow} pt-4 mt-5`}
            style={{ minHeight: "80vh !important" }}
          >
            <h2 className="text-center my-3" style={{ fontSize: "2rem" }}>
              {intl.formatMessage({ id: "contact-information" })}
            </h2>

            <div
              style={{ fontSize: "1rem", color: "black !important" }}
              className={commonPageCss.contactParagraph}
            >
              {intl.formatMessage({
                id: "client-connect-director-contact-information-text",
              })}
            </div>

            <div className="mt-5">
              <h5 className="fw-normal">
                {intl.formatMessage({ id: "name" })}
              </h5>
              <div className="pl-1 mt-1">
                <TextField
                  id="outlined-basic"
                  variant="outlined"
                  placeholder={intl.formatMessage({ id: "enter-your-name" })}
                  value={contactDetail.name}
                  onChange={(e) => updateContactDetails(e, "name")}
                  className="mt-1"
                />
              </div>
              {contactDetailErrors.name.error ? (
                <p className="small text-danger pt-1">
                  {contactDetailErrors.name.message}
                </p>
              ) : null}
            </div>

            <div className="mt-4">
              <h5 className="fw-normal">
                {intl.formatMessage({ id: "email" })}{" "}
                <span style={{ color: "red" }}>*</span>
              </h5>
              <div className="pl-1 mt-1">
                <TextField
                  id="outlined-basic"
                  variant="outlined"
                  placeholder={intl.formatMessage({ id: "enter-your-email" })}
                  value={contactDetail.email}
                  onChange={(e) => updateContactDetails(e, "email")}
                  className="mt-1"
                />
              </div>
              {contactDetailErrors.email.error ? (
                <p className="small text-danger pt-1">
                  {contactDetailErrors.email.message}
                </p>
              ) : null}
            </div>

            <div className="mt-4">
              <h5 className="fw-normal">
                {intl.formatMessage({ id: "contact-no" })}
                {!contactDetailErrors?.emailValid?.error ? (
                  <span style={{ color: "red" }}>*</span>
                ) : (
                  ""
                )}
              </h5>
              <div className="pl-1 mt-1">
                <PhoneNumberInput
                  id="outlined-basic"
                  name="contact-phone"
                  value={contactDetail.phone || ""}
                  defaultCountry={
                    contactDetail.phoneCountry || getCompanyCountryCode()
                  }
                  onChange={(phone, country) => {
                    setContactDetail((prev) => ({
                      ...prev,
                      phone: phone || "",
                      phoneCountry: country || prev.phoneCountry,
                    }));
                    updateContactDetailError("phone", false, "");
                  }}
                />
              </div>
              {contactDetailErrors.phone.error ? (
                <p className="small text-danger pt-1">
                  {contactDetailErrors.phone.message}
                </p>
              ) : null}
            </div>

            <div className="mt-4">
              {contactDetailErrors.emailOrPhone.error ? (
                <p className="small text-danger pt-1">
                  {contactDetailErrors.emailOrPhone.message}
                </p>
              ) : null}
            </div>

            <div className="d-flex justify-content-end align-items-center mt-5 mb-3">
              {/* <Button */}
              {/*   size="large" */}
              {/*   variant="contained" */}
              {/*   color="dark" */}
              {/*   onClick={() => setIsHelpDesk(false)} */}
              {/*   style={{ width: "125px" }} */}
              {/* > */}
              {/*   Skip */}
              {/* </Button> */}

              <Button
                size="large"
                variant="contained"
                color="primary"
                endIcon={<DoubleArrow />}
                onClick={() => saveContactDetails()}
              >
                {intl.formatMessage({ id: "continue" })}
              </Button>
            </div>
          </Paper>
        </div>
      ) : (
        <LoadingOverlay active={loading} spinner>
          <ProgressBar bgcolor={theme.palette.primary.main} />
          <div className={commonPageCss.topHeaderSurvery}>
            <img className={commonPageCss.topLogo} src={logo} alt="logo" />
            <SupportLang />
          </div>
          <BackgroundSecond
            // SF-4956: when opened from Login/Welcome, use the survey they saved; otherwise this page
            // loaded the survey itself (getQuestions) into currentSurvey.
            backgroundImage={
              surveyWithoutLogin
                ? survey_without_login?.survey_background_image
                : currentSurvey?.survey_background_image
            }
          >
            {greetingStatus && (
              <div style={{ width: "100%" }}>
                <AnimatePresence custom={direction}>
                  {allQuestions.length
                    ? allQuestions
                        .slice(trimStart, trimEnd + 1)
                        .map((question, i) => (
                          <Animate direction={direction} key={i}>
                            {question.questionType === "skip" ? null : (
                              <Paper
                                className={`${
                                  question.questionType === "gridspot"
                                    ? commonPageCss.oneQuestionCardWide
                                    : commonPageCss.oneQuestionCard
                                } ${
                                  error?._id === question._id
                                    ? "question-has-error"
                                    : ""
                                }`}
                                style={{ opacity: question.skip && "0.50" }}
                              >
                                {/* {page === 1 && i === 0 ? ( */}
                                {/*   <> */}
                                {/*     {currentSurvey?.description ? ( */}
                                {/*       <div className="text-center text-dark pb-4"> */}
                                {/*         {ReactHtmlParser( */}
                                {/*           currentSurvey.description */}
                                {/*         )} */}
                                {/*       </div> */}
                                {/*     ) : ( */}
                                {/*       <div */}
                                {/*         className="h3 text-center pb-4" */}
                                {/*         style={{ */}
                                {/*           color: */}
                                {/*             survey.survey_theme.primaryColor, */}
                                {/*         }} */}
                                {/*       > */}
                                {/*         Please give us your feedback */}
                                {/*       </div> */}
                                {/*     )} */}
                                {/*   </> */}
                                {/* ) : null} */}
                                <ErrorBanner
                                  show={hasQuestionError(error, question._id)}
                                  message={intl.formatMessage({
                                    id: "please-fill-mandatory-fields-to-continue",
                                  })}
                                />
                                <ErrorBanner
                                  show={hasQuestionError(
                                    writeInError,
                                    question._id,
                                  )}
                                  message={intl.formatMessage({
                                    id: "comment-is-required",
                                  })}
                                />
                                <ErrorBanner
                                  show={hasQuestionError(
                                    imageRequiredError,
                                    question._id,
                                  )}
                                  message={intl.formatMessage({
                                    id: "image-is-required",
                                  })}
                                />
                                <ErrorBanner
                                  show={hasQuestionError(
                                    minError,
                                    question._id,
                                  )}
                                  message={intl.formatMessage(
                                    { id: "minimum-selection-is" },
                                    { min_options: question.min_options },
                                  )}
                                />
                                <ErrorBanner
                                  show={hasQuestionError(
                                    maxError,
                                    question._id,
                                  )}
                                  message={intl.formatMessage(
                                    { id: "maximum-selection-is" },
                                    { max_options: question.max_options },
                                  )}
                                />
                                <ErrorBanner
                                  show={hasQuestionError(
                                    minCharError,
                                    question._id,
                                  )}
                                  message={intl.formatMessage(
                                    { id: "please-enter-at-least" },
                                    { min: question?.minimum_character },
                                  )}
                                />
                                <ErrorBanner
                                  show={hasQuestionError(
                                    emailError,
                                    question._id,
                                  )}
                                  message={intl.formatMessage({
                                    id: "please-enter-valid-email-address",
                                  })}
                                />
                                <ErrorBanner
                                  show={hasQuestionError(
                                    phoneError,
                                    question._id,
                                  )}
                                  message={intl.formatMessage({
                                    id: "invalid-phone-number-format",
                                  })}
                                />
                                <ErrorBanner
                                  show={hasQuestionError(
                                    numberError,
                                    question._id,
                                  )}
                                  message={intl.formatMessage(
                                    {
                                      id: "please-enter-a-number-less-than-or-equal-to",
                                    },
                                    {
                                      max_number_value:
                                        question?.max_number_value,
                                    },
                                  )}
                                />
                                <ErrorBanner
                                  show={hasQuestionError(
                                    gridError,
                                    question._id,
                                  )}
                                  message={intl.formatMessage({
                                    id: "please-fill-all-required-grid-rows",
                                  })}
                                />
                                <ErrorBanner
                                  show={
                                    question.questionType ===
                                      "director_message" &&
                                    Boolean(visitTimeError)
                                  }
                                  message={intl.formatMessage({
                                    id: "please-select-time",
                                  })}
                                />

                                <QuestionWrapper
                                  question={question}
                                  questionType={question.questionType}
                                  isGreyOut={question.skip ?? null}
                                  questionNumber={trimStart + i + 1}
                                  required={question.required}
                                  questionTitle={question.text}
                                  questionSubTitle={question.subText}
                                  showCategory={currentSurvey.show_category}
                                  questionCategory={question.category}
                                  myRef={
                                    error?._id === question._id ||
                                    minError?._id === question._id ||
                                    maxError?._id === question._id
                                      ? myRef
                                      : null
                                  }
                                  hideQuestionNumber={
                                    currentSurvey.hide_question_number
                                  }
                                >
                                  {/* Helper to check if question has any validation error */}
                                  {(() => {
                                    const hasError =
                                      error?._id === question._id ||
                                      writeInError?._id === question._id ||
                                      imageRequiredError?._id ===
                                        question._id ||
                                      minError?._id === question._id ||
                                      maxError?._id === question._id ||
                                      minCharError?._id === question._id ||
                                      emailError?._id === question._id ||
                                      phoneError?._id === question._id ||
                                      numberError?._id === question._id ||
                                      gridError?._id === question._id;

                                    return question.questionType ===
                                      "rating" ? (
                                      <Rating
                                        isGreyOut={question.skip ?? null}
                                        finishSurvey={finishSurvey}
                                        headTonext={(
                                          routeToIndex,
                                          hideIndex,
                                          selectedId,
                                        ) =>
                                          contidionalAutoNext(
                                            routeToIndex,
                                            question,
                                            hideIndex,
                                            selectedId,
                                            question._id,
                                          )
                                        }
                                        question={question}
                                        pageNo={page}
                                        surveyType={surveyType}
                                        allQuestionsOriginal={
                                          allQuestionsOriginal
                                        }
                                        setAllQuestions={setAllQuestions}
                                        setTotalPage={setTotalPage}
                                      />
                                    ) : question.questionType === "radio" ? (
                                      <Radio
                                        isGreyOut={question.skip ?? null}
                                        finishSurvey={finishSurvey}
                                        headTonext={(
                                          routeToIndex,
                                          hideIndex,
                                          selectedId,
                                        ) =>
                                          contidionalAutoNext(
                                            routeToIndex,
                                            question,
                                            hideIndex,
                                            selectedId,
                                            question._id,
                                          )
                                        }
                                        question={question}
                                        pageNo={page}
                                        surveyType={surveyType}
                                        allQuestionsOriginal={
                                          allQuestionsOriginal
                                        }
                                        setAllQuestions={setAllQuestions}
                                        setTotalPage={setTotalPage}
                                        index={i}
                                      />
                                    ) : question.questionType === "gridspot" ? (
                                      <GridSpot
                                        isGreyOut={question.skip ?? null}
                                        headTonext={(
                                          routeToIndex,
                                          hideIndex,
                                          selectedId,
                                        ) =>
                                          contidionalAutoNext(
                                            routeToIndex,
                                            question,
                                            hideIndex,
                                            selectedId,
                                            question._id,
                                          )
                                        }
                                        question={question}
                                        pageNo={page}
                                        surveyType={surveyType}
                                        allQuestionsOriginal={
                                          allQuestionsOriginal
                                        }
                                        setAllQuestions={setAllQuestions}
                                        setTotalPage={setTotalPage}
                                      />
                                    ) : question.questionType === "dropdown" ? (
                                      <Dropdown
                                        hasError={hasError}
                                        isGreyOut={question.skip ?? null}
                                        question={question}
                                        pageNo={page}
                                        surveyType={surveyType}
                                        allQuestionsOriginal={
                                          allQuestionsOriginal
                                        }
                                        setAllQuestions={setAllQuestions}
                                        setTotalPage={setTotalPage}
                                      />
                                    ) : question.questionType === "file" ? (
                                      <Upload
                                        isGreyOut={question.skip ?? null}
                                        question={question}
                                      />
                                    ) : question.questionType === "text" ? (
                                      <TextArea
                                        hasError={hasError}
                                        isGreyOut={question.skip ?? null}
                                        question={question}
                                        allQuestions={allQuestions}
                                        onShowNextQuestion={onShowNextQuestion}
                                        allQuestionsOriginal={
                                          allQuestionsOriginal
                                        }
                                      />
                                    ) : ["multiselect", "skills"].includes(
                                        question.questionType,
                                      ) ? (
                                      <MultipleSelect
                                        isGreyOut={question.skip ?? null}
                                        question={question}
                                        pageNo={page}
                                        surveyType={surveyType}
                                        allQuestionsOriginal={
                                          allQuestionsOriginal
                                        }
                                        setAllQuestions={setAllQuestions}
                                        setTotalPage={setTotalPage}
                                        onMinError={() => setMinError(question)}
                                        onMaxError={() => setMaxError(question)}
                                        onClearErrors={() => {
                                          if (minError?._id === question._id)
                                            setMinError(null);
                                          if (maxError?._id === question._id)
                                            setMaxError(null);
                                        }}
                                        headTonext={(
                                          routeToIndex,
                                          hideIndex,
                                          selectedId,
                                        ) =>
                                          contidionalAutoNext(
                                            routeToIndex,
                                            question,
                                            hideIndex,
                                            selectedId,
                                            question._id,
                                          )
                                        }
                                      />
                                    ) : question.questionType === "sequence" ? (
                                      <Sequence
                                        isGreyOut={question.skip ?? null}
                                        question={question}
                                        surveyType={surveyType}
                                        pageNo={page}
                                        allQuestionsOriginal={
                                          allQuestionsOriginal
                                        }
                                        setAllQuestions={setAllQuestions}
                                        setTotalPage={setTotalPage}
                                        onMinError={() => setMinError(question)}
                                        onMaxError={() => setMaxError(question)}
                                        onClearErrors={() => {
                                          if (minError?._id === question._id)
                                            setMinError(null);
                                          if (maxError?._id === question._id)
                                            setMaxError(null);
                                        }}
                                        headTonext={(
                                          routeToIndex,
                                          hideIndex,
                                          selectedId,
                                        ) =>
                                          contidionalAutoNext(
                                            routeToIndex,
                                            question,
                                            hideIndex,
                                            selectedId,
                                            question._id,
                                          )
                                        }
                                      />
                                    ) : //   : question.questionType === "skills" ? (
                                    //   <Skills
                                    //     isGreyOut={question.skip ?? null}
                                    //     question={question}
                                    //     pageNo={page}
                                    //     surveyType={surveyType}
                                    //     allQuestionsOriginal={allQuestionsOriginal}
                                    //     setAllQuestions={setAllQuestions}
                                    //     setTotalPage={setTotalPage}
                                    //     SkillsData={SkillsData}
                                    //   />
                                    // )
                                    question.questionType === "emoji" ? (
                                      <Emoji
                                        isGreyOut={question.skip ?? null}
                                        headTonext={(
                                          routeToIndex,
                                          hideIndex,
                                          selectedId,
                                        ) =>
                                          contidionalAutoNext(
                                            routeToIndex,
                                            question,
                                            hideIndex,
                                            selectedId,
                                            question._id,
                                          )
                                        }
                                        question={question}
                                        pageNo={page}
                                        surveyType={surveyType}
                                        allQuestionsOriginal={
                                          allQuestionsOriginal
                                        }
                                        setAllQuestions={setAllQuestions}
                                        setTotalPage={setTotalPage}
                                        index={i}
                                      />
                                    ) : question.questionType ===
                                      "employees" ? (
                                      <EmployeeSelect
                                        // headTonext={(
                                        //   routeToIndex,
                                        //   hideIndex,
                                        //   selectedId
                                        // ) =>
                                        //   contidionalAutoNext(
                                        //     routeToIndex,
                                        //     question,
                                        //     hideIndex,
                                        //     selectedId,
                                        //     question._id
                                        //   )
                                        // }
                                        question={question}
                                        loading={empLoading}
                                      />
                                    ) : question.questionType ===
                                      "director_message" ? (
                                      <ClientConnectDirector
                                        clientConnect={
                                          currentSurvey.clientConnect
                                        }
                                        leadershipUser={
                                          currentSurvey.leadershipUser
                                        }
                                        value={directorMessage}
                                        onChange={(e) =>
                                          addDirectorMessage_(e.target.value)
                                        }
                                        isVisitInformation={
                                          currentSurvey?.visit_information
                                        }
                                        isSurveyCompetition={
                                          currentSurvey?.competitionInformation
                                        }
                                        selectedTimeOut={selectedTimeOut}
                                        setSelectedTimeOut={(time) => {
                                          setSelectedTimeOut(time);
                                          setVisitTimeError(false);
                                        }}
                                        contactDetail={contactDetail}
                                        contactDetailErrors={
                                          contactDetailErrors
                                        }
                                        updateCustomValues={updateCustomValues}
                                        customValuesErrors={customValuesErrors}
                                        competitionInfoText={
                                          currentSurvey?.competition_information_text
                                        }
                                        customFields={
                                          currentSurvey?.customValues
                                        }
                                        customValues={customValues}
                                      />
                                    ) : question.questionType ===
                                      "star_rating" ? (
                                      <StarRating
                                        isGreyOut={question.skip ?? null}
                                        question={question}
                                      />
                                    ) : question.questionType === "number" ? (
                                      <NumberType
                                        hasError={hasError}
                                        isGreyOut={question.skip ?? null}
                                        question={question}
                                      />
                                    ) : question.questionType ===
                                      "handwrittenInput" ? (
                                      <SignatureInput
                                        isGreyOut={question.skip ?? null}
                                        question={question}
                                      />
                                    ) : question.questionType ===
                                      "inventory" ? (
                                      <InventoryType
                                        hasError={hasError}
                                        isGreyOut={question.skip ?? null}
                                        question={question}
                                      />
                                    ) : question.questionType === "grid" ? (
                                      <GridType
                                        hasError={hasError}
                                        isGreyOut={question.skip ?? null}
                                        question={question}
                                        gridHideQuestions={gridHideQuestions}
                                      />
                                    ) : (
                                      ""
                                    );
                                  })()}

                                  {question.questionType !==
                                    "director_message" &&
                                  (question?.allow_comment ||
                                    question?.allow_file_upload) ? (
                                    <NotesAndImage
                                      isGreyOut={question.skip ?? null}
                                      question={question}
                                    />
                                  ) : null}
                                </QuestionWrapper>
                              </Paper>
                            )}
                          </Animate>
                        ))
                    : null}
                </AnimatePresence>
              </div>
            )}

            {/* Contact Form Page */}
            {showContactForm && page === totalPage && greetingStatus && (
              <div style={{ width: "100%" }}>
                <Paper className={commonPageCss.oneQuestionCard}>
                  <ContactForm
                    contactFormConfig={{
                      contact_info_title: currentSurvey?.contact_info_title,
                      contact_info_description:
                        currentSurvey?.contact_info_description,
                      contact_fields: currentSurvey?.contact_fields,
                      contact_custom_fields:
                        currentSurvey?.contact_custom_fields,
                    }}
                    contactFormData={contactFormData}
                    updateContactFormData={updateContactFormData}
                    contactFormErrors={contactFormErrors}
                  />
                </Paper>
              </div>
            )}

            <div className={commonPageCss.BgBottomProgressBar}>
              <div className={commonPageCss.BottomProgress}>
                <BottomProgress
                  answered={hasClientConnect ? page + 2 : page}
                  total={hasClientConnect ? totalPage + 2 : totalPage}
                  submitDisabled={loading}
                  showSaveAndExit={survey_without_login?.allowPartialResponse}
                  saveAndExitTitle={intl.formatMessage({
                    id: "save-and-exit",
                  })}
                  saveAndExitDisabled={loading}
                  onSaveAndExit={handleSaveAndExit}
                  PrevisDisable={
                    loading ||
                    (page === 1 &&
                      !hasClientConnect &&
                      (isCompanyLogin() ? surveyCount === 1 : true))
                  }
                  handlePrev={() => {
                    if (loading) {
                      return;
                    }
                    if (hasClientConnect && page === 1) {
                      history.push({
                        pathname: "/survey/client_connect",
                        state: { surveyWithoutLogin: true, fromQuestion: true },
                      });
                    }
                    if (page !== 1) {
                      setGs(false);
                      setPage(page - 1);
                      setDirection(-1);
                    }
                    if (isCompanyLogin() && page === 1 && surveyCount > 1) {
                      history.push({
                        pathname: "/survey/audition",
                        state: { surveyWithoutLogin: survey },
                      });
                    }
                  }}
                  nextTitle={
                    !(totalPage !== page)
                      ? intl.formatMessage({ id: "submit" })
                      : intl.formatMessage({ id: "next-question" })
                  }
                  handleNext={() => {
                    if (loading) {
                      return;
                    }
                    let notAnswered = notAnsweredQ();
                    let minOptionCheck = minOptionChecked();
                    let maxOptionCheck = maxOptionChecked();
                    let minCharCheck = minCharacterChecked();
                    let emailChecked = emailTypeChecked();
                    let phoneChecked = phoneTypeChecked();
                    let numberChecked = numberTypeChecked();
                    let gridChecked = gridTypeChecked();
                    let notWriteIN = notAnsweredWriteInOption();
                    let imageRequired = imageRequiredOption();

                    if (
                      notAnswered === undefined &&
                      minOptionCheck === undefined &&
                      maxOptionCheck === undefined &&
                      minCharCheck === undefined &&
                      emailChecked === undefined &&
                      phoneChecked === undefined &&
                      numberChecked === undefined &&
                      gridChecked === undefined &&
                      notWriteIN === undefined &&
                      imageRequired === undefined
                    ) {
                      if (totalPage !== page) {
                        savePartialAnswers();
                        const currentQuestion = allQuestions.slice(
                          trimStart,
                          trimEnd + 1,
                        );
                        if (
                          currentQuestion[0]?.questionType ===
                          "director_message"
                        ) {
                          const hasErrors = validateCompetitionCustomValues();
                          if (hasErrors) {
                            return;
                          } else {
                            if (
                              currentSurvey?.visit_information &&
                              selectedTimeOut === null
                            ) {
                              setVisitTimeError(true);
                              return;
                            } else {
                              setGs(false);
                              setPage(page + 1);
                              setDirection(1);
                              window.scrollTo({
                                top: 0,
                                behavior: "smooth", // for smooth scrolling
                              });
                            }
                          }
                        } else {
                          setGs(false);
                          setPage(page + 1);
                          setDirection(1);
                          window.scrollTo({
                            top: 0,
                            behavior: "smooth", // for smooth scrolling
                          });
                        }
                      } else {
                        if (
                          currentSurvey?.visit_information &&
                          selectedTimeOut === null
                        ) {
                          setVisitTimeError(true);
                        } else {
                          submitAnswers();
                        }
                      }
                    } else if (notAnswered) {
                      setError(notAnswered);
                      myRef?.current?.scrollIntoView();
                    } else if (minOptionCheck) {
                      setMinError(minOptionCheck);
                      myRef?.current?.scrollIntoView();
                    } else if (maxOptionCheck) {
                      setMaxError(maxOptionCheck);
                      myRef?.current?.scrollIntoView();
                    } else if (minCharCheck) {
                      setMinCharError(minCharCheck);
                      myRef?.current?.scrollIntoView();
                    } else if (emailChecked) {
                      setEmailError(emailChecked);
                      myRef?.current?.scrollIntoView();
                    } else if (phoneChecked) {
                      setPhoneError(phoneChecked);
                      myRef?.current?.scrollIntoView();
                    } else if (numberChecked) {
                      setNumberError(numberChecked);
                      myRef?.current?.scrollIntoView();
                    } else if (gridChecked) {
                      setGridError(gridChecked);
                      myRef?.current?.scrollIntoView();
                    } else if (notWriteIN) {
                      setWriteInError(notWriteIN);
                      myRef?.current?.scrollIntoView();
                    } else if (imageRequired) {
                      setImageRequiredError(imageRequired);
                      myRef?.current?.scrollIntoView();
                    }
                  }}
                />
              </div>
            </div>
          </BackgroundSecond>
        </LoadingOverlay>
      )}
      {showErrorPopup?.show && (
        <AutoSubmitErrorPopup
          showErrorPopup={showErrorPopup}
          onClose={() => {
            handleClosePage();
          }}
        />
      )}
    </>
  );
}
// Function that extracts the necessary state from the Redux store
const mapStateToProps = (state) => {
  return {
    answeres: state.survey.answeres,
    locations: state.survey.locations,
    survey_without_login: state.survey.survey_without_login,
    survey_theme: state.survey.survey_theme,
    survey: state.survey,
    directorMessage: state.survey.directorMessage,
    contactDetails: state.survey.contactDetails,
  };
};
// Function that binds the dispatch to action creators
const mapDispatchToProps = (dispatch) => {
  return {
    addContactDetails_: (data) => dispatch(addContactDetails(data)),
    addDirectorMessage_: (data) => dispatch(addDirectorMessage(data)),
    addAnswer_: (data) => dispatch(addAnswer(data)),
    removeAnswer_: (data) => dispatch(removeAnswer(data)),
    setSurveyTheme_: (data) => dispatch(setSurveyTheme(data)),
    clearAnsweres_: () => dispatch(clearAnsweres()),
  };
};
// Connect Question Component to the Redux store
export default connect(mapStateToProps, mapDispatchToProps)(Question);
